// Domain Engine — multi-domain trend discovery with localStorage persistence

export interface Domain {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgGradient: string;
  sampleKeywords: string[];
  url?: string;
}

export interface DiscoveredTrend {
  id: string;
  domainId: string;
  name: string;
  description: string;
  category: string;
  popularity: number;   // 0–100
  growthRate: number;   // percentage
  keywords: string[];
  status: 'emerging' | 'trending' | 'stable' | 'declining';
  sentiment: 'positive' | 'neutral' | 'negative';
  relevanceScore: number; // 0–100
  sources: string[];
  discoveredAt: string;
}

export interface DomainSession {
  domain: Domain;
  customLabel?: string;
  trends: DiscoveredTrend[];
  selectedTrendIds: string[];
  discoveredAt: string;
}

// ── Preset Domains ──────────────────────────────────────────────────────────

export const PRESET_DOMAINS: Domain[] = [
  // ── Content & Marketing ──────────────────────────────────────────────────
  {
    id: 'technical-writing',
    label: 'Technical Writing',
    description: 'Docs-as-code, API docs, developer guides, DITA, style guides',
    icon: '📝',
    color: '#60a5fa',
    bgGradient: 'from-blue-500/20 to-cyan-500/10',
    sampleKeywords: ['docs-as-code', 'API documentation', 'structured authoring', 'DITA', 'developer experience'],
  },
  {
    id: 'digital-marketing',
    label: 'Digital Marketing',
    description: 'SEO, content strategy, paid media, conversion optimisation, analytics',
    icon: '📈',
    color: '#f472b6',
    bgGradient: 'from-pink-500/20 to-rose-500/10',
    sampleKeywords: ['zero-click search', 'AI-generated content', 'first-party data', 'performance marketing', 'CRO'],
  },
  {
    id: 'social-media-marketing',
    label: 'Social Media Marketing',
    description: 'Short-form video, creator economy, community-led growth, platform shifts',
    icon: '📱',
    color: '#a78bfa',
    bgGradient: 'from-violet-500/20 to-purple-500/10',
    sampleKeywords: ['short-form video', 'creator economy', 'social commerce', 'community marketing', 'UGC'],
  },
  {
    id: 'agentic-ai-docs',
    label: 'Agentic AI in Documentation',
    description: 'LLM-powered doc generation, AI writing assistants, autonomous content pipelines',
    icon: '🤖',
    color: '#34d399',
    bgGradient: 'from-emerald-500/20 to-teal-500/10',
    sampleKeywords: ['LLM documentation', 'AI writing', 'autonomous agents', 'RAG for docs', 'prompt engineering'],
  },
  {
    id: 'product-management',
    label: 'Product Management',
    description: 'Product strategy, roadmapping, OKRs, PLG, AI-assisted product discovery',
    icon: '🗺️',
    color: '#fbbf24',
    bgGradient: 'from-amber-500/20 to-yellow-500/10',
    sampleKeywords: ['product-led growth', 'outcome-based roadmaps', 'AI product discovery', 'jobs-to-be-done', 'continuous discovery'],
  },
  {
    id: 'developer-relations',
    label: 'Developer Relations',
    description: 'DevRel strategy, developer experience, community building, OSS advocacy',
    icon: '👩‍💻',
    color: '#f97316',
    bgGradient: 'from-orange-500/20 to-red-500/10',
    sampleKeywords: ['developer experience', 'DevRel metrics', 'community-led growth', 'OSS strategy', 'API evangelism'],
  },
  // ── Hospitality & Travel ─────────────────────────────────────────────────
  {
    id: 'hospitality',
    label: 'Hospitality & Hotels',
    description: 'Hotel operations, guest experience, revenue management, luxury travel, F&B',
    icon: '🏨',
    color: '#f59e0b',
    bgGradient: 'from-amber-500/20 to-orange-500/10',
    sampleKeywords: ['contactless check-in', 'revenue management', 'guest personalization', 'sustainability', 'OTA strategy'],
  },
  {
    id: 'travel-tourism',
    label: 'Travel & Tourism',
    description: 'Destination marketing, airline industry, cruise, adventure tourism, visa trends',
    icon: '✈️',
    color: '#38bdf8',
    bgGradient: 'from-sky-500/20 to-blue-500/10',
    sampleKeywords: ['overtourism', 'regenerative travel', 'digital nomads', 'visa-free destinations', 'travel insurance'],
  },
  // ── Healthcare & Wellness ─────────────────────────────────────────────────
  {
    id: 'healthcare',
    label: 'Healthcare & Medicine',
    description: 'Clinical care, digital health, telemedicine, hospital operations, pharma',
    icon: '🏥',
    color: '#34d399',
    bgGradient: 'from-emerald-500/20 to-green-500/10',
    sampleKeywords: ['telemedicine', 'AI diagnostics', 'value-based care', 'hospital-at-home', 'clinical AI'],
  },
  {
    id: 'wellness-fitness',
    label: 'Wellness & Fitness',
    description: 'Health clubs, spa industry, mental wellness, wearables, nutrition trends',
    icon: '💪',
    color: '#4ade80',
    bgGradient: 'from-green-500/20 to-teal-500/10',
    sampleKeywords: ['recovery tech', 'longevity', 'mental wellness', 'wearable health', 'functional nutrition'],
  },
  // ── Retail & E-Commerce ───────────────────────────────────────────────────
  {
    id: 'retail',
    label: 'Retail & E-Commerce',
    description: 'Brick-and-mortar, omnichannel, supply chain, consumer trends, fast fashion',
    icon: '🛍️',
    color: '#f472b6',
    bgGradient: 'from-pink-500/20 to-fuchsia-500/10',
    sampleKeywords: ['omnichannel retail', 'social commerce', 'recommerce', 'same-day delivery', 'experiential retail'],
  },
  {
    id: 'luxury-goods',
    label: 'Luxury Goods & Fashion',
    description: 'Luxury brands, high fashion, resale market, NFT fashion, sustainability',
    icon: '💎',
    color: '#c084fc',
    bgGradient: 'from-purple-500/20 to-violet-500/10',
    sampleKeywords: ['resale luxury', 'quiet luxury', 'metaverse fashion', 'Gen Z luxury', 'sustainable couture'],
  },
  // ── Finance & Banking ─────────────────────────────────────────────────────
  {
    id: 'fintech',
    label: 'Fintech & Banking',
    description: 'Digital banking, payments, lending, insurtech, embedded finance, crypto',
    icon: '🏦',
    color: '#60a5fa',
    bgGradient: 'from-blue-500/20 to-indigo-500/10',
    sampleKeywords: ['embedded finance', 'BNPL', 'open banking', 'stablecoin', 'neobank'],
  },
  {
    id: 'investment-wealth',
    label: 'Investment & Wealth Management',
    description: 'Asset management, private equity, robo-advisors, ESG investing, family offices',
    icon: '📊',
    color: '#fbbf24',
    bgGradient: 'from-yellow-500/20 to-amber-500/10',
    sampleKeywords: ['ESG investing', 'alternative assets', 'tokenized assets', 'wealth tech', 'retail investing'],
  },
  // ── Real Estate & Construction ────────────────────────────────────────────
  {
    id: 'real-estate',
    label: 'Real Estate',
    description: 'Residential, commercial, proptech, REITs, urban development, housing market',
    icon: '🏗️',
    color: '#fb923c',
    bgGradient: 'from-orange-500/20 to-red-500/10',
    sampleKeywords: ['proptech', 'build-to-rent', 'smart buildings', 'co-living', 'housing affordability'],
  },
  // ── Education & EdTech ────────────────────────────────────────────────────
  {
    id: 'education-edtech',
    label: 'Education & EdTech',
    description: 'K-12, higher education, online learning, LMS platforms, skills training',
    icon: '🎓',
    color: '#818cf8',
    bgGradient: 'from-indigo-500/20 to-violet-500/10',
    sampleKeywords: ['microlearning', 'AI tutoring', 'skills-based hiring', 'online degrees', 'LXP platforms'],
  },
  // ── Food & Beverage ───────────────────────────────────────────────────────
  {
    id: 'food-beverage',
    label: 'Food & Beverage',
    description: 'Restaurant industry, food delivery, CPG, alternative proteins, food tech',
    icon: '🍽️',
    color: '#f97316',
    bgGradient: 'from-orange-500/20 to-amber-500/10',
    sampleKeywords: ['ghost kitchens', 'alternative protein', 'food waste', 'farm-to-table', 'beverage innovation'],
  },
  // ── Manufacturing & Supply Chain ──────────────────────────────────────────
  {
    id: 'manufacturing',
    label: 'Manufacturing & Industry',
    description: 'Smart factories, Industry 4.0, supply chain, robotics, lean manufacturing',
    icon: '🏭',
    color: '#94a3b8',
    bgGradient: 'from-slate-500/20 to-gray-500/10',
    sampleKeywords: ['Industry 4.0', 'digital twin', 'nearshoring', 'predictive maintenance', 'collaborative robots'],
  },
  // ── HR & People ───────────────────────────────────────────────────────────
  {
    id: 'human-resources',
    label: 'HR & People Operations',
    description: 'Talent acquisition, employee engagement, DEI, workforce planning, HR tech',
    icon: '👥',
    color: '#2dd4bf',
    bgGradient: 'from-teal-500/20 to-cyan-500/10',
    sampleKeywords: ['skills-based hiring', 'employee wellbeing', 'HR automation', 'DEI strategy', 'workforce analytics'],
  },
  // ── Legal & Compliance ────────────────────────────────────────────────────
  {
    id: 'legal-legaltech',
    label: 'Legal & LegalTech',
    description: 'Law firm management, contract automation, AI in law, compliance, IP',
    icon: '⚖️',
    color: '#a8a29e',
    bgGradient: 'from-stone-500/20 to-gray-500/10',
    sampleKeywords: ['AI contract review', 'legal automation', 'e-discovery', 'regulatory compliance', 'LegalTech SaaS'],
  },
  // ── Sustainability & CleanTech ─────────────────────────────────────────────
  {
    id: 'sustainability',
    label: 'Sustainability & ESG',
    description: 'ESG reporting, net zero, circular economy, renewable energy, green supply chain',
    icon: '🌿',
    color: '#4ade80',
    bgGradient: 'from-green-500/20 to-emerald-500/10',
    sampleKeywords: ['net zero', 'carbon credits', 'ESG reporting', 'circular economy', 'green hydrogen'],
  },
  // ── Media & Entertainment ─────────────────────────────────────────────────
  {
    id: 'media-entertainment',
    label: 'Media & Entertainment',
    description: 'Streaming, gaming, sports, live events, podcasting, creator economy',
    icon: '🎬',
    color: '#e879f9',
    bgGradient: 'from-fuchsia-500/20 to-pink-500/10',
    sampleKeywords: ['streaming wars', 'live sports rights', 'creator economy', 'AI content', 'gaming monetization'],
  },
  // ── Automotive & Mobility ─────────────────────────────────────────────────
  {
    id: 'automotive-mobility',
    label: 'Automotive & Mobility',
    description: 'EV adoption, autonomous vehicles, mobility-as-a-service, supply chain, dealers',
    icon: '🚗',
    color: '#38bdf8',
    bgGradient: 'from-sky-500/20 to-blue-500/10',
    sampleKeywords: ['EV adoption', 'autonomous vehicles', 'MaaS', 'connected cars', 'EV charging infrastructure'],
  },
];

// ── Rich Domain-Specific Trend Seeds ────────────────────────────────────────

const TREND_SEEDS: Record<string, Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>> = {

  // ── HOSPITALITY & HOTELS ──────────────────────────────────────────────────
  'hospitality': [
    {
      name: 'AI-Powered Dynamic Revenue Management',
      category: 'Revenue Management',
      description: 'Hotel revenue managers are deploying AI platforms like IDeaS G3 and Duetto that ingest real-time demand signals, competitor rates, and macroeconomic indicators to make autonomous pricing decisions across room types, reducing manual intervention by 60% while increasing RevPAR by up to 12%.',
      keywords: ['revenue management', 'dynamic pricing', 'RevPAR optimisation', 'demand forecasting', 'IDeaS'],
      sources: ['Hotels Magazine', 'HospitalityNet', 'HSMAI Revenue Optimization Conference'],
    },
    {
      name: 'Hyper-Personalised Guest Experience at Scale',
      category: 'Guest Experience',
      description: 'Luxury chains like Four Seasons and Marriott Bonvoy are leveraging CRM-integrated AI to deliver personalised room configurations, dietary preferences, and curated local itineraries before guests arrive — moving from reactive to anticipatory hospitality that drives repeat bookings and Net Promoter Scores above 70.',
      keywords: ['guest personalisation', 'CRM integration', 'pre-arrival experience', 'loyalty programmes', 'guest data platform'],
      sources: ['Cornell Hospitality Quarterly', 'Skift', 'Hotel Management Magazine'],
    },
    {
      name: 'Contactless & Biometric Check-In Revolution',
      category: 'Technology Operations',
      description: 'Following post-pandemic acceleration, mobile check-in and digital key adoption has reached 45% of branded hotel guests globally. Hilton\'s Digital Key and Marriott\'s mobile app set the standard, while biometric facial recognition check-in is being piloted by Accor and IHG to eliminate front desk queues entirely.',
      keywords: ['contactless check-in', 'digital key', 'biometric ID', 'mobile hospitality', 'frictionless arrival'],
      sources: ['American Hotel & Lodging Association', 'Phocuswire', 'Oracle Hospitality'],
    },
    {
      name: 'Bleisure & Extended Stay Segment Growth',
      category: 'Market Segments',
      description: 'The blending of business and leisure travel (bleisure) has reshaped demand patterns — with remote workers extending corporate trips into week-long stays. Marriott, Hyatt, and Hilton have all launched extended-stay sub-brands, and average length of stay in urban markets has grown 23% since 2023.',
      keywords: ['bleisure travel', 'extended stay', 'work-from-hotel', 'remote work travel', 'long-stay rates'],
      sources: ['Global Business Travel Association', 'Skift Research', 'JLL Hotels & Hospitality'],
    },
    {
      name: 'Hotel Sustainability Certifications Drive Bookings',
      category: 'Sustainability',
      description: 'Green Key and EarthCheck certifications are increasingly influencing corporate travel booking decisions, with Booking.com data showing that eco-labelled properties command a 7-11% rate premium. Marriott\'s "Serve 360" programme and Accor\'s net-zero roadmap to 2050 are setting industry benchmarks for scope 1, 2, and 3 emissions.',
      keywords: ['Green Key certification', 'net-zero hotels', 'sustainable hospitality', 'EarthCheck', 'corporate sustainability travel'],
      sources: ['Green Key Global', 'Sustainable Hospitality Alliance', 'Booking.com Sustainability Report'],
    },
    {
      name: 'F&B as Destination Driver for Urban Hotels',
      category: 'Food & Beverage',
      description: 'Hotel restaurants and rooftop bars are no longer amenities — they\'re primary revenue drivers attracting local clientele. Nobu Hotels, 1 Hotels, and Edition Hotels generate up to 40% of total revenue from F&B, partnering with Michelin-starred chefs and local producers to create authentic culinary destinations that justify premium room rates.',
      keywords: ['hotel restaurants', 'destination F&B', 'rooftop bar strategy', 'celebrity chef partnership', 'local F&B sourcing'],
      sources: ['Nation\'s Restaurant News', 'Hotel F&B', 'ICHRIE Journal'],
    },
  ],

  // ── TRAVEL & TOURISM ─────────────────────────────────────────────────────
  'travel-tourism': [
    {
      name: 'Regenerative Tourism Replacing Sustainable Travel',
      category: 'Sustainability',
      description: 'The concept of leaving destinations better than you found them is gaining mainstream traction, with destinations like Bhutan, Costa Rica, and New Zealand positioning their tourism economies around ecological restoration, community wealth distribution, and cultural preservation — moving beyond the "do no harm" standard.',
      keywords: ['regenerative tourism', 'responsible travel', 'community-based tourism', 'ecotourism 2.0', 'destination stewardship'],
      sources: ['World Tourism Organization', 'Travel + Leisure', 'Condé Nast Traveler'],
    },
    {
      name: 'Overtourism Management & Visitor Economy Reform',
      category: 'Destination Management',
      description: 'Barcelona, Amsterdam, and Venice are implementing tourist caps, day-tripper surcharges, and cruise ship restrictions to address overtourism. The EU\'s new tourism directive requires destination management organisations to develop carrying capacity frameworks by 2027, fundamentally reshaping visitor flow management.',
      keywords: ['overtourism', 'visitor caps', 'destination management', 'tourist tax', 'carrying capacity'],
      sources: ['UNWTO', 'European Tourism Commission', 'Skift'],
    },
    {
      name: 'AI Travel Agents Disrupting OTA Model',
      category: 'Technology',
      description: 'Agentic AI travel planners — Anthropic\'s Claude integrated into Expedia, Google\'s Gemini in Google Flights, and standalone tools like Layla — are enabling end-to-end trip planning with real-time availability and booking in conversational interfaces, threatening the traditional OTA display advertising revenue model.',
      keywords: ['AI travel agent', 'agentic booking', 'conversational travel', 'OTA disruption', 'trip planning AI'],
      sources: ['Phocuswire', 'Skift Research', 'Travel Technology Association'],
    },
    {
      name: 'Medical & Wellness Tourism Boom',
      category: 'Niche Segments',
      description: 'Medical tourism market reached $54B in 2025, driven by dental, cosmetic surgery, and IVF seekers from the US, UK, and Gulf states travelling to Thailand, Turkey, and India. Wellness retreats offering longevity programmes, sleep clinics, and biohacking experiences command $5,000+ per week and are sold out 6 months in advance.',
      keywords: ['medical tourism', 'wellness retreats', 'longevity travel', 'dental tourism', 'health tourism'],
      sources: ['Medical Tourism Association', 'Global Wellness Institute', 'Wellness Tourism Worldwide'],
    },
    {
      name: 'Digital Nomad Infrastructure Investment',
      category: 'Market Segments',
      description: 'Over 35 countries now offer digital nomad visas (up from 7 in 2020), with Portugal, Spain, UAE, and Costa Rica leading. Destinations are investing in coliving hubs, high-speed connectivity, and remote work communities to capture the $787B annual spend of the global digital nomad population.',
      keywords: ['digital nomad visa', 'remote work travel', 'coliving spaces', 'nomad infrastructure', 'work-from-anywhere'],
      sources: ['Nomad List', 'A-List Destinations', 'International Labour Organization'],
    },
    {
      name: 'Cruise Industry\'s Gen Z & Millennial Pivot',
      category: 'Market Transformation',
      description: 'Cruise lines are aggressively targeting under-45 travellers: Virgin Voyages\' adults-only model, Celebrity\'s Edge class, and Norwegian\'s Freestyle Cruising have reshaped the industry\'s age demographics. Short "cruise-cation" sailings of 2-4 nights from home ports are capturing first-time cruisers at record rates.',
      keywords: ['younger cruisers', 'cruise innovation', 'cruise-cation', 'Virgin Voyages', 'expedition cruising'],
      sources: ['Cruise Lines International Association', 'Seatrade Cruise', 'Travel Weekly'],
    },
  ],

  // ── HEALTHCARE ───────────────────────────────────────────────────────────
  'healthcare': [
    {
      name: 'AI Diagnostic Imaging in Clinical Practice',
      category: 'Clinical AI',
      description: 'FDA-cleared AI tools from companies like Viz.ai, Aidoc, and PathAI are being deployed in radiology, pathology, and cardiology departments to detect anomalies in medical imaging with sensitivity matching senior radiologists. Epic\'s AI integration marketplace has seen 300+ validated clinical AI tools deployed across US health systems in 2025.',
      keywords: ['AI radiology', 'clinical AI', 'medical imaging AI', 'FDA-cleared AI', 'diagnostic automation'],
      sources: ['Journal of the American Medical Association', 'Radiology', 'Health Affairs'],
    },
    {
      name: 'Hospital-at-Home Programme Expansion',
      category: 'Care Models',
      description: 'CMS\'s Acute Hospital Care at Home waiver has been adopted by 300+ US hospitals, enabling acute-level care delivered in patients\' homes via remote monitoring, daily clinician visits, and telehealth. Mayo Clinic and Massachusetts General report 26% lower 30-day readmission rates and significantly higher patient satisfaction scores.',
      keywords: ['hospital at home', 'acute home care', 'remote patient monitoring', 'CMS waiver', 'decentralised care'],
      sources: ['New England Journal of Medicine', 'American Hospital Association', 'Advisory Board'],
    },
    {
      name: 'Value-Based Care Contracting Acceleration',
      category: 'Payment Models',
      description: 'CMS\'s goal of having 100% of Medicare beneficiaries in accountable care relationships by 2030 is driving mass adoption of value-based contracts. ACO REACH, MSSP, and commercial bundled payment programmes are shifting hospital revenue from volume to outcomes — requiring new population health infrastructure and risk stratification capabilities.',
      keywords: ['value-based care', 'ACO', 'bundled payments', 'population health', 'risk stratification'],
      sources: ['Modern Healthcare', 'Health Affairs', 'CMS Innovation Center'],
    },
    {
      name: 'GLP-1 Drugs Reshaping Healthcare Economics',
      category: 'Pharmaceuticals',
      description: 'The explosive growth of GLP-1 receptor agonists (Ozempic, Wegovy, Mounjaro) is fundamentally reshaping healthcare economics — reducing demand for bariatric surgery by 30%, lowering cardiovascular event rates, and creating $50B+ in annual global sales by 2026. Payers, hospitals, and pharma are recalibrating entire service lines around this pharmacological shift.',
      keywords: ['GLP-1 drugs', 'Ozempic', 'obesity medicine', 'weight loss drugs', 'cardiometabolic health'],
      sources: ['New England Journal of Medicine', 'Stat News', 'Kaiser Family Foundation'],
    },
    {
      name: 'Interoperability & Health Data Exchange',
      category: 'Health IT',
      description: 'The ONC\'s information-blocking rule and CMS interoperability mandates are forcing EHR vendors (Epic, Oracle Health) and payers to open patient data via FHIR APIs. The emergence of TEFCA\'s national health data network is enabling seamless patient record exchange — a prerequisite for AI-powered population health management.',
      keywords: ['FHIR', 'health data interoperability', 'TEFCA', 'EHR integration', 'patient data portability'],
      sources: ['ONC Health IT', 'HIMSS', 'Healthcare IT News'],
    },
    {
      name: 'Mental Health Parity & Digital Therapeutics',
      category: 'Mental Health',
      description: 'New mental health parity enforcement is pressuring insurers to cover digital therapeutics (DTx) for depression, anxiety, and substance use disorder. FDA-cleared apps like Freespira and Pear Therapeutics predecessors are gaining formulary placement, while employer wellness platforms (Spring Health, Lyra Health) now serve 10M+ covered lives.',
      keywords: ['digital therapeutics', 'mental health parity', 'DTx', 'behavioral health tech', 'employer mental health'],
      sources: ['American Psychological Association', 'JMIR Mental Health', 'McKinsey Health Institute'],
    },
  ],

  // ── WELLNESS & FITNESS ───────────────────────────────────────────────────
  'wellness-fitness': [
    {
      name: 'Longevity Science Enters Mainstream Fitness',
      category: 'Longevity',
      description: 'Peter Attia\'s "Outlive" framework, Bryan Johnson\'s Blueprint protocol, and the proliferation of longevity clinics are moving VO2 max testing, DEXA scans, and continuous glucose monitoring from elite athletes to affluent consumers. Life sciences-backed fitness brands like Equinox are pivoting their programming toward lifespan extension metrics.',
      keywords: ['longevity fitness', 'VO2 max', 'continuous glucose monitor', 'health span', 'biohacking'],
      sources: ['Longevity Medicine', 'Peter Attia MD', 'Global Wellness Institute'],
    },
    {
      name: 'Recovery Technology as Primary Offering',
      category: 'Recovery',
      description: 'Cold plunge, infrared sauna, hyperbaric oxygen, and compression therapy are no longer gym extras — they\'re primary revenue drivers. RESTORE Hyper Wellness now operates 200+ locations, and recovery-focused studios are commanding $75-150 per session. Whoop and Garmin recovery scores are influencing member workout scheduling.',
      keywords: ['recovery tech', 'cold plunge', 'infrared sauna', 'HRV tracking', 'Whoop recovery'],
      sources: ['Global Wellness Summit', 'ClubIntel', 'IHRSA'],
    },
    {
      name: 'AI Personal Training & Adaptive Workouts',
      category: 'Technology',
      description: 'AI coaching platforms like Whoop Coach, Future Fit, and Tonal\'s AI trainer are delivering real-time, adaptive training programmes that adjust intensity, volume, and exercise selection based on recovery data, performance history, and stated goals — threatening the traditional personal trainer model at the $50-100/session tier.',
      keywords: ['AI personal training', 'adaptive workouts', 'Tonal', 'Whoop Coach', 'personalised fitness'],
      sources: ['ACSM Health & Fitness Journal', 'Club Industry', 'Morning Consult'],
    },
    {
      name: 'Mental Wellness Integration in Physical Studios',
      category: 'Mental Wellness',
      description: 'Boutique studios like Equinox, Mindbody-managed spaces, and meditation chain Headspace House are integrating mental wellness programming — breathwork, sound bath, float tanks, and therapeutic yoga — into physical fitness facilities, recognising that members who use mental wellness services show 2x higher retention rates.',
      keywords: ['mental wellness', 'breathwork', 'meditation studios', 'float therapy', 'holistic wellness'],
      sources: ['Mindbody Wellness Index', 'Global Wellness Institute', 'IDEA Health & Fitness'],
    },
    {
      name: 'Weight Loss Drug Impact on Fitness Industry',
      category: 'Market Shift',
      description: 'GLP-1 medication adoption is creating both challenges and opportunities for gyms: members on Ozempic/Wegovy show lower cardiovascular endurance but increased motivation to build muscle mass. IHRSA data shows fitness facilities near medically-supervised weight loss clinics see 18% higher new member acquisition.',
      keywords: ['Ozempic fitness', 'GLP-1 members', 'weight loss community', 'body recomposition', 'fitness market shift'],
      sources: ['IHRSA', 'ACSM', 'Journal of Obesity'],
    },
    {
      name: 'Functional Nutrition & Supplement Personalisation',
      category: 'Nutrition',
      description: 'DNA-based nutrition plans (Nutrigenomix), microbiome testing (Viome), and AI-powered supplement stacking are moving into the mainstream wellness market. The global functional nutrition market is projected at $275B by 2026, with personalised protocols replacing generic supplementation advice.',
      keywords: ['personalised nutrition', 'microbiome testing', 'nutrigenomics', 'functional supplements', 'sports nutrition'],
      sources: ['Nutrition Business Journal', 'Global Wellness Institute', 'Journal of Nutrition'],
    },
  ],

  // ── RETAIL & E-COMMERCE ──────────────────────────────────────────────────
  'retail': [
    {
      name: 'Recommerce & Resale Market Explosive Growth',
      category: 'Circular Economy',
      description: 'The secondhand retail market is projected to reach $350B globally by 2028, growing 3x faster than traditional retail. ThredUp, Depop, and Vinted are challenging traditional retail, while luxury incumbents like Burberry and Patagonia have launched in-house resale platforms. Returns management has become a strategic resale revenue channel.',
      keywords: ['recommerce', 'secondhand fashion', 'resale platforms', 'circular retail', 're-commerce'],
      sources: ['ThredUp Resale Report', 'McKinsey Retail', 'Euromonitor International'],
    },
    {
      name: 'Hyper-Localised Same-Day Delivery Wars',
      category: 'Logistics & Fulfilment',
      description: 'Amazon\'s 1-hour delivery in 100+ US cities, Walmart\'s drone delivery expansion, and rapid grocery apps (Getir, Gopuff) are making same-day a baseline expectation. Micro-fulfilment centres embedded within retail stores — pioneered by Kroger and Albertsons — are becoming standard infrastructure investments for tier-1 retailers.',
      keywords: ['same-day delivery', 'micro-fulfilment', 'drone delivery', 'dark stores', 'last-mile logistics'],
      sources: ['Retail Dive', 'Chain Store Age', 'Business Insider Retail'],
    },
    {
      name: 'AI Merchandising & Demand Sensing',
      category: 'Operations & Technology',
      description: 'Retailers like Zara, H&M, and Walmart are deploying AI demand-sensing platforms that reduce markdown rates by 15-20% by predicting micro-demand patterns at SKU-store level. Autonomous reordering, AI planogramming, and computer vision shelf analytics are becoming standard category management tools.',
      keywords: ['AI merchandising', 'demand sensing', 'inventory optimisation', 'planogram AI', 'markdown reduction'],
      sources: ['Retail Technology Innovation Hub', 'WWD', 'National Retail Federation'],
    },
    {
      name: 'Experiential Retail Flagship Investment',
      category: 'Store Formats',
      description: 'Physical retail is investing in experience over inventory: Apple\'s Forum stores, Nike House of Innovation, and LEGO\'s certified stores generate 3-4x the sales per square foot of standard outlets. Interactive product demonstrations, maker spaces, personalisation bars, and event programming are redefining the role of the physical store.',
      keywords: ['experiential retail', 'flagship stores', 'retail experience', 'store-as-destination', 'phygital retail'],
      sources: ['CBRE Retail', 'Deloitte Retail Insights', 'WWD'],
    },
    {
      name: 'Social Commerce TikTok Shop Dominance',
      category: 'Social Commerce',
      description: 'TikTok Shop surpassed $20B in US GMV in 2025, with live-stream commerce sessions driving impulse purchases across beauty, fashion, and electronics. Meta\'s Shops integration and Pinterest\'s product tagging are forcing every brand to build native in-app purchase capabilities and creator commerce partnerships.',
      keywords: ['TikTok Shop', 'live commerce', 'shoppable video', 'creator storefronts', 'social commerce ROI'],
      sources: ['eMarketer', 'Shopify Commerce Trends', 'Business of Fashion'],
    },
    {
      name: 'Retail Media Networks Become Profit Centres',
      category: 'Advertising & Media',
      description: 'Walmart Connect, Target Roundel, Kroger Precision Marketing, and Amazon Advertising collectively generate $50B+ in annual ad revenue, with operating margins of 70-80%. Every major retailer is building a media network — transforming their customer data and digital shelf into high-margin advertising inventory that subsidises competitive pricing.',
      keywords: ['retail media', 'sponsored products', 'first-party data advertising', 'retail advertising ROI', 'in-store media'],
      sources: ['Digiday', 'eMarketer Retail Media', 'Forrester Retail Media Report'],
    },
  ],

  // ── LUXURY GOODS & FASHION ───────────────────────────────────────────────
  'luxury-goods': [
    {
      name: 'Quiet Luxury Aesthetic Driving Timeless Demand',
      category: 'Consumer Trends',
      description: 'The "quiet luxury" movement — exemplified by The Row, Loro Piana, and Brunello Cucinelli — prioritises understated quality over logo-heavy branding, resonating with HNW consumers fatigued by conspicuous consumption. Brands embracing heritage craftsmanship, neutral palettes, and investment dressing are outperforming logo-centric competitors.',
      keywords: ['quiet luxury', 'stealth wealth', 'investment dressing', 'Loro Piana', 'old money aesthetic'],
      sources: ['Business of Fashion', 'Vogue Business', 'Lyst Index'],
    },
    {
      name: 'Luxury Resale Authentication Technology',
      category: 'Resale & Authentication',
      description: 'LVMH\'s Aura Blockchain Consortium, Entrupy\'s AI authentication, and the Vestiaire Collective\'s direct-to-consumer model are professionalising luxury resale. CHLOE and Richemont\'s Watchfinder validate secondary market value, while "digital product passports" required by EU regulation from 2026 are transforming luxury provenance.',
      keywords: ['luxury authentication', 'blockchain provenance', 'digital product passport', 'luxury resale', 'LVMH Aura'],
      sources: ['Business of Fashion', 'The RealReal Investor Relations', 'EU Circular Economy Action Plan'],
    },
    {
      name: 'Gen Z Luxury Entry Through Accessories & Beauty',
      category: 'Generational Strategy',
      description: 'Luxury houses are engaging Gen Z through accessible entry points: Miu Miu\'s $500 ballet flats, Prada Beauty\'s skincare launch, and Jacquemus\'s viral bag campaigns. Gen Z\'s luxury spend is projected at $600B by 2030, with social media discovery (TikTok, Pinterest) replacing traditional editorial gatekeepers.',
      keywords: ['Gen Z luxury', 'luxury entry price points', 'luxury accessories', 'Miu Miu strategy', 'luxury beauty'],
      sources: ['Bain & Company Luxury Report', 'McKinsey Luxury', 'Condé Nast International'],
    },
    {
      name: 'Experiential Luxury & Private Client Events',
      category: 'Client Experience',
      description: 'LVMH, Kering, and Richemont are competing for ultra-high-net-worth clients through exclusive experiences: private fashion shows, atelier visits, bespoke commissions, and brand-owned wellness retreats. Chanel\'s "Les Beiges Beach" pop-up and Dior\'s spa in Paris generate earned media and deepen client emotional bonds beyond product transactions.',
      keywords: ['luxury experiences', 'private client events', 'bespoke luxury', 'VIC programmes', 'ultra luxury'],
      sources: ['Business of Fashion', 'Luxury Daily', 'Wealth-X'],
    },
    {
      name: 'Sustainability as Luxury Brand Pillar',
      category: 'Sustainability',
      description: 'Stella McCartney\'s plant-based cashmere, Hermès\'s Mycoworks mushroom leather "Sylvania", and Gucci\'s Off the Grid collection are demonstrating that sustainability can enhance luxury positioning. LVMH\'s LIFE 360 programme commits to 100% eco-design by 2030, with certifiable sustainable practices becoming a premium price justifier.',
      keywords: ['sustainable luxury', 'bio-based materials', 'Hermès Mycoworks', 'LVMH LIFE 360', 'eco-luxury'],
      sources: ['Positive Luxury', 'Business of Fashion Sustainability', 'Kering Sustainability Report'],
    },
    {
      name: 'Watches & Jewellery Investment Supercycle',
      category: 'Hard Luxury',
      description: 'Patek Philippe, Rolex, and AP watches continue to appreciate 15-40% annually on secondary markets, attracting a new class of collector-investor. Watches of Switzerland\'s pre-owned certified programme, Christie\'s digital auctions, and the Geneva Watch Days demonstrate how hard luxury is positioning itself as an alternative asset class.',
      keywords: ['luxury watches', 'watch investment', 'Patek Philippe', 'pre-owned certified', 'hard luxury'],
      sources: ['Morgan Stanley Luxury Report', 'WatchPro', 'Christie\'s Watch Auctions'],
    },
  ],

  // ── FINTECH & BANKING ────────────────────────────────────────────────────
  'fintech': [
    {
      name: 'Embedded Finance Penetrating Every Industry',
      category: 'Embedded Finance',
      description: 'Non-financial companies are embedding banking, lending, and insurance directly into their customer journeys: Shopify Balance, Uber Money, Walmart\'s One Finance app. Stripe Treasury, Unit, and Synapse provide the BaaS infrastructure enabling this shift, with embedded finance projected to generate $7T in US transactions by 2027.',
      keywords: ['embedded finance', 'banking-as-a-service', 'BaaS', 'financial super app', 'non-bank financial services'],
      sources: ['Andreessen Horowitz Fintech Report', 'CB Insights Fintech Trends', 'McKinsey Global Banking'],
    },
    {
      name: 'Real-Time Payments Infrastructure Race',
      category: 'Payments',
      description: 'The Fed\'s FedNow launch, expanding RTP network adoption, and the EU\'s Instant Credit Transfer regulation are driving the end of batch payment processing. Zelle processed $1T in 2025, Venmo Business is approaching PayPal\'s commercial volumes, and ISO 20022 migration is forcing correspondent banking modernisation globally.',
      keywords: ['FedNow', 'real-time payments', 'RTP network', 'ISO 20022', 'instant payments'],
      sources: ['Federal Reserve Payments Study', 'NACHA', 'SWIFT Institute'],
    },
    {
      name: 'AI Credit Decisioning & Fair Lending Compliance',
      category: 'Credit & Lending',
      description: 'Upstart, Zest AI, and Pagaya are deploying ML credit models that consider alternative data (rent payments, utility bills) alongside traditional FICO scores, expanding credit access to thin-file borrowers. The CFPB\'s AI model explainability requirements and fair lending scrutiny are forcing lenders to build bias-tested, auditable decision models.',
      keywords: ['AI credit scoring', 'alternative credit data', 'CFPB compliance', 'fair lending AI', 'Upstart model'],
      sources: ['American Banker', 'CFPB Research', 'Oliver Wyman Fintech'],
    },
    {
      name: 'Stablecoin Payments Reaching Enterprise Scale',
      category: 'Digital Assets',
      description: 'Circle\'s USDC processed $12T in 2025, and Visa\'s stablecoin settlement on Solana is making enterprise adoption tangible. PayPal\'s PYUSD, Stripe\'s stablecoin payments API, and bank-issued stablecoins under the GENIUS Act are legitimising stablecoins as a cross-border B2B payment rail competing with SWIFT.',
      keywords: ['stablecoin payments', 'USDC', 'PYUSD', 'cross-border payments', 'GENIUS Act'],
      sources: ['The Block', 'Coindesk', 'Bank for International Settlements'],
    },
    {
      name: 'Open Banking Data Monetisation Strategies',
      category: 'Open Banking',
      description: 'With the Consumer Financial Protection Bureau\'s Section 1033 rule taking effect, US banks must provide permissioned data access to third parties. Plaid, MX, and Akoya are building data monetisation platforms, while banks like JPMorgan and Wells Fargo are developing data-as-a-service businesses that turn compliance obligation into revenue.',
      keywords: ['open banking', 'Section 1033', 'financial data sharing', 'Plaid', 'data monetisation'],
      sources: ['CFPB', 'Finovate', 'American Banker'],
    },
    {
      name: 'Agentic AI in Wealth Management & Advisory',
      category: 'Wealth Tech',
      description: 'Goldman Sachs\'s GS AI assistant, Morgan Stanley\'s AI Next Best Action, and Betterment\'s AI financial advisor are deploying agentic AI to deliver institutional-quality portfolio analysis at mass affluent price points. AI advisors managing $500B+ in AUM are pushing incumbents to accelerate human-AI hybrid advisory models.',
      keywords: ['AI wealth management', 'robo-advisor 2.0', 'Morgan Stanley AI', 'hybrid advisory', 'agentic finance'],
      sources: ['Financial Planning Magazine', 'Barron\'s Advisor', 'BCG Wealth Management'],
    },
  ],

  // ── REAL ESTATE ──────────────────────────────────────────────────────────
  'real-estate': [
    {
      name: 'Build-to-Rent Institutional Investment Wave',
      category: 'Residential',
      description: 'Institutional investors (Blackstone, Invitation Homes, Greystar) have accumulated $550B+ in single-family rental assets, professionalising the rental housing market with smart home technology, amenity packages, and data-driven maintenance. Build-to-rent communities with on-site services are outperforming traditional apartments on occupancy and NOI growth.',
      keywords: ['build-to-rent', 'single-family rental', 'institutional BTR', 'Greystar', 'SFR portfolio'],
      sources: ['Urban Land Institute', 'CBRE Research', 'Yardi Matrix'],
    },
    {
      name: 'PropTech AI Transforming CRE Transactions',
      category: 'PropTech',
      description: 'VTS Data, CoStar\'s AI lease comp tool, and JLL\'s Azara platform are deploying AI to automate lease abstraction, predict sublease availability, and identify off-market acquisitions. In residential markets, Offerpad and Opendoor\'s iBuying algorithms are being rebuilt as AI-assisted pricing tools for traditional brokerages.',
      keywords: ['AI lease abstraction', 'proptech platforms', 'VTS', 'CoStar AI', 'CRE transaction tech'],
      sources: ['CoStar Group Research', 'PropTech Insider', 'JLL Research'],
    },
    {
      name: 'Office-to-Residential Conversion Accelerating',
      category: 'Adaptive Reuse',
      description: 'With office vacancy rates above 20% in major US cities, adaptive reuse conversions are receiving unprecedented municipal incentives. NYC\'s conversion programme targeting 30,000 units, Chicago\'s LaSalle Street revival, and LA\'s ADU ordinance are reshaping central business districts, with federal tax credits making previously uneconomic conversions viable.',
      keywords: ['office-to-residential', 'adaptive reuse', 'commercial conversion', 'vacancy crisis', 'adaptive rehab'],
      sources: ['CBRE Adaptive Reuse Report', 'Urban Land Institute', 'NAHB'],
    },
    {
      name: 'Climate Risk Pricing Disrupting Property Values',
      category: 'Climate & Risk',
      description: 'First Street Foundation\'s climate risk scores, state-level flood zone remapping, and insurance market withdrawal from California and Florida coastal markets are creating a repricing event for climate-exposed real estate. Properties in high-risk zones are experiencing 15-30% valuation haircuts as lenders incorporate climate risk into underwriting models.',
      keywords: ['climate risk real estate', 'flood zone pricing', 'insurance withdrawal', 'First Street Foundation', 'climate-adjusted cap rate'],
      sources: ['First Street Foundation', 'Urban Land Institute Climate', 'CBRE Climate Risk Report'],
    },
    {
      name: 'Multifamily Amenity Arms Race Cooling',
      category: 'Multifamily Operations',
      description: 'After years of amenity escalation, Class A multifamily operators are shifting from amenity quantity to programming quality. CoStar data shows that pet parks, co-working spaces, and pickleball courts have commoditised — operators achieving NOI outperformance are investing in hospitality-trained staff, community programming, and resident experience technology.',
      keywords: ['multifamily amenities', 'resident experience', 'apartment community building', 'NOI optimisation', 'renter retention'],
      sources: ['National Multifamily Housing Council', 'Apartments.com Trends', 'CoStar Multifamily'],
    },
    {
      name: 'Data Centre Real Estate Supercycle',
      category: 'Industrial & Alternative',
      description: 'AI infrastructure demand has created a data centre construction supercycle: $100B in announced investment in 2025 alone, with Equinix, Digital Realty, and hyperscaler-owned campuses expanding into Tier 2 markets. Power procurement, cooling technology, and fibre connectivity have become the primary constraints — and investment thesis — for data centre REIT portfolios.',
      keywords: ['data centre REITs', 'AI infrastructure real estate', 'hyperscaler campuses', 'data centre power', 'Digital Realty'],
      sources: ['CBRE Data Center Trends', 'JLL Data Centers', 'Datacenter Dynamics'],
    },
  ],

  // ── EDUCATION & EDTECH ───────────────────────────────────────────────────
  'education-edtech': [
    {
      name: 'AI Tutoring Reaching Every Student',
      category: 'AI in Education',
      description: 'Khan Academy\'s Khanmigo, Duolingo\'s AI tutor, and Carnegie Learning\'s MATHia are delivering personalised, Socratic tutoring at scale. Gates Foundation research shows AI tutoring platforms achieve learning outcomes equivalent to 1-on-1 human tutoring in 60% less time. Concerns about academic integrity are driving new AI detection and curriculum design responses.',
      keywords: ['AI tutoring', 'Khanmigo', 'personalised learning', 'intelligent tutoring systems', 'adaptive learning'],
      sources: ['EdSurge', 'EDUCAUSE Review', 'Gates Foundation Education Report'],
    },
    {
      name: 'Skills-Based Hiring Undermining Degree Requirement',
      category: 'Workforce Transformation',
      description: 'IBM, Google, Apple, and Accenture have removed 4-year degree requirements for the majority of their roles, driving demand for micro-credentials, bootcamp certificates, and competency-based assessments. LinkedIn\'s Skills Graph and Workday\'s skills-based talent marketplace are shifting employer-education alignment from credentials to demonstrated capability.',
      keywords: ['skills-based hiring', 'micro-credentials', 'competency-based education', 'degree alternatives', 'skills taxonomy'],
      sources: ['Burning Glass Institute', 'Harvard Business Review', 'LinkedIn Workforce Report'],
    },
    {
      name: 'Generative AI Reshaping Curriculum Design',
      category: 'Curriculum Innovation',
      description: 'Higher education institutions are rapidly integrating AI literacy into core curricula: MIT\'s Responsible AI framework, Stanford\'s AI-assisted course design tools, and UK\'s QAA AI guidance are setting standards. The UNESCO Global Education Coalition is developing an AI competency framework for K-12 that 40+ countries are adopting.',
      keywords: ['AI curriculum', 'AI literacy education', 'responsible AI training', 'curriculum redesign', 'UNESCO AI competency'],
      sources: ['UNESCO', 'Chronicle of Higher Education', 'EDUCAUSE'],
    },
    {
      name: 'Online Degree Credentialing Maturation',
      category: 'Higher Education Models',
      description: 'Arizona State University Online (200K+ students), Southern New Hampshire University, and Western Governors University have demonstrated that quality online degrees can be delivered at $10-15K total cost. Coursera Degrees, edX\'s Professional Certificates, and Guild Education\'s employer-funded degree partnerships are institutionalising the online education market.',
      keywords: ['online degrees', 'competency-based degrees', 'employer-funded education', 'Western Governors University', 'Coursera degrees'],
      sources: ['Inside Higher Ed', 'Coursera Industry Skills Report', 'American Council on Education'],
    },
    {
      name: 'Learning Experience Platforms Replacing LMS',
      category: 'Enterprise Learning',
      description: 'Corporate learning is migrating from traditional LMS platforms (Cornerstone, Saba) to Learning Experience Platforms (LXPs) like Degreed, EdCast, and LinkedIn Learning that deliver personalised, continuous learning paths aggregated from multiple content providers. Workday Learning\'s AI recommendation engine and Microsoft Viva Learning\'s Teams integration are making learning ambient.',
      keywords: ['LXP platforms', 'enterprise learning', 'Degreed', 'continuous learning', 'AI learning paths'],
      sources: ['Brandon Hall Group', 'Fosway Group', 'Corporate Executive Board'],
    },
    {
      name: 'Neurodiversity-Inclusive EdTech Design',
      category: 'Accessibility & Inclusion',
      description: 'ADHD, dyslexia, and autism-aware design is becoming a commercial differentiator in EdTech: tools like Lexia Learning, Co:Writer, and Microsoft Immersive Reader are demonstrating that universal design for learning benefits all students. The 1-in-5 prevalence of learning differences is driving schools and parents to demand evidence-based, differentiated digital tools.',
      keywords: ['neurodiversity education', 'UDL', 'ADHD edtech', 'dyslexia tools', 'inclusive design learning'],
      sources: ['International Dyslexia Association', 'National Center for Learning Disabilities', 'CAST UDL'],
    },
  ],

  // ── FOOD & BEVERAGE ──────────────────────────────────────────────────────
  'food-beverage': [
    {
      name: 'Alternative Protein Mainstream Menu Integration',
      category: 'Food Innovation',
      description: 'Beyond Meat and Impossible Foods have crossed the chasm into QSR and food service mainstream, while cultivated meat pioneers UPSIDE Foods and GOOD Meat are scaling US restaurant partnerships. Fermentation-derived proteins (Quorn, Every Company\'s egg white alternative) are increasingly competitive on taste, texture, and — critically — cost parity with animal proteins.',
      keywords: ['alternative protein', 'plant-based food', 'cultivated meat', 'food innovation', 'fermentation protein'],
      sources: ['Good Food Institute', 'Technomic', 'Food Business News'],
    },
    {
      name: 'Ghost Kitchen Consolidation & Maturation',
      category: 'Restaurant Operations',
      description: 'After the ghost kitchen gold rush, the market is consolidating: Kitchen United (acquired by Kroger), Reef Technology\'s restructuring, and Eatsa\'s pivot signal a maturation phase. Operators that survive are those with proprietary delivery-native brands, sophisticated unit economics, and real estate partnerships with grocery chains and QSR operators.',
      keywords: ['ghost kitchens', 'virtual restaurants', 'dark kitchens', 'delivery-only brands', 'kitchen consolidation'],
      sources: ['Nation\'s Restaurant News', 'QSR Magazine', 'Restaurant Business Online'],
    },
    {
      name: 'Functional & "Better-for-You" Beverage Boom',
      category: 'Beverage Innovation',
      description: 'Athletic Brewing (non-alcoholic craft beer), Olipop and Poppi (functional soda), Liquid Death (water marketing), and Recess (adaptogen beverages) have collectively raised $1B+ and are reshaping the $250B non-alcoholic beverage market. Gen Z and Millennial "sober curious" consumers are driving double-digit category growth across functional wellness drinks.',
      keywords: ['non-alcoholic beverages', 'functional drinks', 'adaptogen beverages', 'Athletic Brewing', 'sober curious'],
      sources: ['Beverage Industry Magazine', 'SPINS', 'Nielsen IQ Beverage Report'],
    },
    {
      name: 'Food Waste Technology & Circular F&B',
      category: 'Sustainability',
      description: 'Too Good To Go (75M users) and OLIO\'s community redistribution model are mainstreaming food waste reduction as a consumer behaviour. At the supply chain level, Apeel Sciences\' plant-derived coating extends produce shelf life by 3x, while Rubicon\'s waste analytics platform helps restaurants reduce food waste by 22% on average through AI-powered purchasing forecasts.',
      keywords: ['food waste reduction', 'Too Good To Go', 'Apeel Sciences', 'circular food economy', 'supply chain sustainability'],
      sources: ['ReFED', 'Food Waste Reduction Alliance', 'WRAP Food Waste Reports'],
    },
    {
      name: 'Restaurant Automation Addressing Labour Costs',
      category: 'Restaurant Technology',
      description: 'Miso Robotics\'s Flippy arm at White Castle, Bear Robotics\'s Servi robot at Applebee\'s, and Nuro\'s delivery robots are demonstrating ROI as labour costs reach 35-40% of restaurant revenue. Toast and Square POS integrations with AI scheduling and predictive ordering are becoming table-stakes for chains managing labour efficiency.',
      keywords: ['restaurant robotics', 'kitchen automation', 'Flippy robot', 'Bear Robotics', 'labour cost reduction'],
      sources: ['Nation\'s Restaurant News', 'Restaurant Technology News', 'Toast Restaurant Trends'],
    },
    {
      name: 'Premiumisation of Everyday Food Categories',
      category: 'Consumer Trends',
      description: 'Consumers are trading up in affordable categories: $6 oat milk lattes, $18 artisan chocolate bars, $28 hot sauce sets. Fly By Jing, Brightland Olive Oil, and Fishwife canned fish have built premium CPG brands at DTC scale. Nielsen reports a "lipstick effect" in food — consumers cutting discretionary spend but maintaining small food premiums during economic uncertainty.',
      keywords: ['food premiumisation', 'artisan CPG', 'DTC food brands', 'premium grocery', 'affordable luxury food'],
      sources: ['Nielsen IQ Premium Report', 'Mintel Food Trends', 'Forbes Retail & Grocery'],
    },
  ],

  // ── MANUFACTURING ────────────────────────────────────────────────────────
  'manufacturing': [
    {
      name: 'Nearshoring & Supply Chain Regionalisation',
      category: 'Supply Chain Strategy',
      description: 'US manufacturing reshoring reached a record $264B in 2025, driven by CHIPS Act, Inflation Reduction Act incentives, and geopolitical risk reduction. Mexico\'s nearshoring boom (Tesla gigafactory in Monterrey, BMW plant expansion) and "China+1" strategies are fundamentally restructuring global supply chains toward regional production hubs.',
      keywords: ['nearshoring', 'reshoring', 'supply chain resilience', 'CHIPS Act manufacturing', 'Mexico manufacturing'],
      sources: ['Reshoring Initiative', 'Boston Consulting Group Supply Chain', 'Manufacturing USA'],
    },
    {
      name: 'Digital Twin Deployment in Smart Factories',
      category: 'Industry 4.0',
      description: 'Siemens\' Xcelerator platform, PTC\'s ThingWorx, and NVIDIA Omniverse are enabling full digital twin deployments where every machine, process, and product variant can be simulated before physical production. BMW\'s "digital factory twin" reduced commissioning time by 30%, while Lockheed Martin\'s F-35 programme demonstrates defence manufacturing applications.',
      keywords: ['digital twin', 'smart factory', 'Siemens Xcelerator', 'NVIDIA Omniverse', 'simulation manufacturing'],
      sources: ['Manufacturing Leadership Council', 'MIT Industrial Performance Center', 'Siemens Digital Industries'],
    },
    {
      name: 'Collaborative Robots Transforming Factory Floors',
      category: 'Robotics & Automation',
      description: 'Cobot shipments grew 27% in 2025, with Universal Robots, ABB, and FANUC deploying arms that safely work alongside human operators without safety cages. SME adoption is accelerating as cobots achieve ROI in 12-18 months for assembly, quality inspection, and material handling tasks — democratising automation beyond automotive and electronics sectors.',
      keywords: ['collaborative robots', 'cobots', 'Universal Robots', 'human-robot collaboration', 'flexible automation'],
      sources: ['International Federation of Robotics', 'A3 Association for Advancing Automation', 'SME Manufacturing'],
    },
    {
      name: 'AI-Powered Predictive Maintenance at Scale',
      category: 'Maintenance & Operations',
      description: 'Industrial IoT platforms (Uptake, SparkCognition, GE\'s Predix) are deploying vibration, thermal, and acoustic sensors across production lines to predict equipment failures 14-30 days in advance. Rolls-Royce\'s "Power by the Hour" and Caterpillar\'s Condition Monitoring demonstrate how predictive maintenance becomes a service revenue model.',
      keywords: ['predictive maintenance', 'IIoT', 'condition monitoring', 'equipment uptime', 'sensor analytics'],
      sources: ['Deloitte Manufacturing Report', 'Industry Week', 'LNS Research'],
    },
    {
      name: 'Green Manufacturing & Scope 3 Emissions Pressure',
      category: 'Sustainability',
      description: 'SEC climate disclosure rules and EU CSRD are forcing manufacturers to measure and reduce Scope 3 supply chain emissions. Apple\'s supplier clean energy programme, BMW\'s green steel procurement, and Unilever\'s supplier sustainability scorecard are setting the bar — manufacturers failing ESG standards risk losing major brand contracts.',
      keywords: ['green manufacturing', 'Scope 3 emissions', 'industrial decarbonisation', 'sustainable supply chain', 'green steel'],
      sources: ['Manufacturing Leadership Council', 'Industrial Decarbonization Roadmap', 'Rocky Mountain Institute'],
    },
    {
      name: 'Additive Manufacturing for End-Use Production',
      category: 'Advanced Manufacturing',
      description: 'Metal 3D printing has moved from prototyping to end-use production: GE Aerospace prints 100,000+ fuel nozzle components annually, Relativity Space is 3D printing entire rocket structures, and Desktop Metal\'s binder jetting enables high-volume production of complex geometries. AM is reshaping spare parts logistics — printing on demand versus warehousing.',
      keywords: ['additive manufacturing', 'metal 3D printing', 'GE Aerospace 3D printing', 'industrial 3D printing', 'AM production'],
      sources: ['Additive Manufacturing Magazine', 'Wohlers Associates Report', 'AMPOWER'],
    },
  ],

  // ── HUMAN RESOURCES ─────────────────────────────────────────────────────
  'human-resources': [
    {
      name: 'Skills-Based Talent Architecture Replacing Job Titles',
      category: 'Talent Strategy',
      description: 'IBM, Unilever, and Mastercard have replaced job-title-based HR architectures with skills ontologies — enabling internal mobility, succession planning, and workforce planning based on verified capability profiles. Workday\'s Skills Cloud and SAP SuccessFactors Skills are making skills-based talent management an enterprise standard.',
      keywords: ['skills-based organisation', 'skills ontology', 'internal mobility', 'Workday Skills Cloud', 'talent architecture'],
      sources: ['Deloitte Global Human Capital Trends', 'SHRM', 'McKinsey Talent'],
    },
    {
      name: 'AI-Augmented Recruitment & Bias Mitigation',
      category: 'Talent Acquisition',
      description: 'Paradox\'s Olivia AI recruiter conducts screening conversations at scale, while HireVue\'s structured AI assessments and Pymetrics\' bias-audited neuroscience games are reducing time-to-hire by 40%. EEOC guidance on AI hiring tools is driving demand for explainable, auditable AI models that can demonstrate non-discrimination compliance.',
      keywords: ['AI recruiting', 'Paradox AI', 'bias-free hiring', 'HireVue', 'structured hiring'],
      sources: ['SHRM AI Recruiting Report', 'Harvard Business Review Hiring', 'EEOC Guidance on AI'],
    },
    {
      name: 'Employee Wellbeing ROI Measurement',
      category: 'Employee Wellbeing',
      description: 'Employers are moving beyond EAP access to measuring wellbeing programme ROI: Spring Health shows $3.60 return per $1 invested in mental health, Lyra Health demonstrates reduced absenteeism, and Virgin Pulse\'s whole-person health platform links biometric data to claims costs. CFOs are demanding evidence-based wellbeing investment frameworks.',
      keywords: ['employee wellbeing ROI', 'mental health benefits', 'Spring Health', 'workplace wellness', 'wellbeing metrics'],
      sources: ['Willis Towers Watson', 'Mercer Global Talent Trends', 'Harvard Business Review Wellness'],
    },
    {
      name: 'Hybrid Work Policy Stabilisation',
      category: 'Future of Work',
      description: 'After years of experimentation, 65% of knowledge workers are now in stable hybrid arrangements (2-3 days in office). Companies enforcing 5-day RTO (Amazon, Goldman Sachs) are seeing voluntary attrition spikes, while flexible hybrid policies correlate with 22% higher employee retention. "Flexible by design" workplace strategies are becoming the competitive talent differentiator.',
      keywords: ['hybrid work policy', 'RTO mandate', 'flexible work', 'workplace flexibility', 'hybrid work retention'],
      sources: ['Stanford WFH Research', 'Gallup Workplace Report', 'Microsoft Work Trend Index'],
    },
    {
      name: 'DEI Strategy Under Political & Legal Scrutiny',
      category: 'Diversity & Inclusion',
      description: 'Following the US Supreme Court\'s affirmative action ruling and state-level DEI legislation, corporations are reframing DEI programmes around "skills equity," "belonging," and "workforce accessibility" — maintaining commitment while adapting language and measurement. SHRM\'s shift from DEI to "IE&D" (Inclusion, Equity, and Diversity) reflects this recalibration.',
      keywords: ['DEI strategy 2026', 'workplace inclusion', 'belonging programmes', 'pay equity', 'DEI compliance'],
      sources: ['SHRM', 'Diversity Inc', 'McKinsey Diversity Report'],
    },
    {
      name: 'HR Technology Consolidation & AI Feature Race',
      category: 'HR Technology',
      description: 'Workday, SAP, and Oracle dominate enterprise HR tech, but AI-native challengers (Rippling, Lattice, Leapsome) are forcing incumbent platform consolidation. Salesforce\'s HR integration with Einstein AI, Microsoft Copilot for HR, and Workday Illuminate represent the AI feature race to make HR administrative tasks fully autonomous by 2027.',
      keywords: ['HRIS AI', 'Workday Illuminate', 'HR automation', 'people analytics', 'HR platform consolidation'],
      sources: ['Sapient Insights HR Systems Survey', 'Gartner HR Technology', 'Josh Bersin Academy'],
    },
  ],

  // ── LEGAL & LEGALTECH ────────────────────────────────────────────────────
  'legal-legaltech': [
    {
      name: 'Generative AI Contract Review in Law Firms',
      category: 'AI in Law',
      description: 'Harvey AI (backed by $100M from Sequoia and OpenAI) is deployed at Allen & Overy, PwC Legal, and A&O Shearman for contract review, due diligence, and legal research — reducing associate hours on NDA and M&A document review by 50-70%. Bar associations are issuing guidance on competence obligations when using generative AI.',
      keywords: ['Harvey AI', 'AI contract review', 'legal AI', 'AI due diligence', 'law firm AI adoption'],
      sources: ['American Bar Association', 'Law.com', 'LegalTech News'],
    },
    {
      name: 'Alternative Legal Service Provider Growth',
      category: 'Legal Market',
      description: 'Deloitte Legal, Axiom, and Elevate Services are capturing legal work traditionally handled by BigLaw at 40-60% lower cost. The Big Four\'s legal managed services practices each surpassed $2B in revenue in 2025. Corporate legal departments are adopting "legal operations" frameworks (CLOC) to manage vendor relationships and technology spend.',
      keywords: ['ALSP', 'legal managed services', 'Deloitte Legal', 'Axiom legal', 'legal operations CLOC'],
      sources: ['Thomson Reuters Legal Tracker', 'CLOC State of the Industry', 'Law.com'],
    },
    {
      name: 'E-Discovery AI and Digital Evidence Complexity',
      category: 'Litigation',
      description: 'Generative AI communications (ChatGPT conversations, Copilot documents) are creating new e-discovery challenges as courts establish rules for AI-generated evidence preservation. Relativity\'s aiR for Review and Nuix\'s NLP tools are processing multi-modal evidence (Teams recordings, Slack conversations) — fundamentally expanding discovery cost and complexity.',
      keywords: ['AI e-discovery', 'digital evidence', 'Relativity AI', 'ESI preservation', 'AI litigation'],
      sources: ['Sedona Conference', 'Electronic Discovery Reference Model', 'Law Technology Today'],
    },
    {
      name: 'Regulatory Compliance as Product Strategy',
      category: 'Regulatory',
      description: 'EU AI Act compliance, SEC cybersecurity disclosure rules, and ESG reporting mandates are creating a $50B+ RegTech market. Firms like Droit, ACA Group, and NAVEX are building compliance-as-a-service platforms that turn regulatory obligation into competitive positioning for financial services, healthcare, and technology companies.',
      keywords: ['RegTech', 'EU AI Act compliance', 'regulatory technology', 'ESG compliance', 'NAVEX'],
      sources: ['Thomson Reuters Regulatory Intelligence', 'Compliance Week', 'IAPP Privacy Law'],
    },
    {
      name: 'Law Firm Pricing Models Under Client Pressure',
      category: 'Law Firm Management',
      description: 'Fortune 500 GCs are demanding alternative fee arrangements (AFAs) for 40%+ of outside counsel work. Value-based billing, legal project management, and fixed-fee litigation are replacing the billable hour in transactional and regulatory practice areas. Clio\'s legal billing data shows firms offering AFAs grow 3x faster than hourly-only practices.',
      keywords: ['alternative fee arrangements', 'legal pricing', 'value-based billing', 'fixed fee legal', 'legal project management'],
      sources: ['Wolters Kluwer ELM Solutions', 'BTI Consulting', 'Clio Legal Trends Report'],
    },
    {
      name: 'Legal Design & Access to Justice Technology',
      category: 'Access to Justice',
      description: 'DoNotPay\'s consumer legal assistant model (despite setbacks), Joshua Browder\'s legal chatbots, and Courtroom5\'s pro se litigation platform are addressing the $90B access-to-justice gap in the US. Law school clinics at Stanford and MIT are designing legal aid tools, while LSC-funded legal tech is reaching 1.5M low-income users annually.',
      keywords: ['legal design', 'access to justice', 'legal aid technology', 'pro se litigation', 'legal chatbots'],
      sources: ['American Bar Foundation', 'Stanford Law School CodeX', 'Legal Services Corporation'],
    },
  ],

  // ── SUSTAINABILITY & ESG ─────────────────────────────────────────────────
  'sustainability': [
    {
      name: 'Mandatory ESG Reporting Reshaping Corporate Strategy',
      category: 'Regulation & Compliance',
      description: 'The EU\'s CSRD now requires 50,000+ companies to produce verified sustainability reports using ESRS standards — including scope 3 value chain emissions, biodiversity impact, and social metrics. SEC\'s climate disclosure rules and ISSB\'s IFRS S1/S2 standards are creating global ESG reporting convergence, making sustainability data as rigorous as financial accounting.',
      keywords: ['CSRD', 'ESG reporting', 'ISSB standards', 'scope 3 reporting', 'SEC climate disclosure'],
      sources: ['Global Reporting Initiative', 'IFRS Foundation', 'Environmental Finance'],
    },
    {
      name: 'Voluntary Carbon Market Integrity Crisis',
      category: 'Carbon Markets',
      description: 'High-profile investigations into REDD+ forest offsets and Gold Standard-certified credits have triggered a VCM credibility crisis. The Integrity Council for the Voluntary Carbon Market (ICVCM) Core Carbon Principles and VCMI\'s Claims Code are attempting to restore trust. Companies like Microsoft, Shopify, and Stripe are pivoting to carbon removal (Frontier programme) over avoidance offsets.',
      keywords: ['carbon credits', 'VCM integrity', 'ICVCM standards', 'carbon removal', 'net zero claims'],
      sources: ['Carbon Brief', 'Bloomberg Green', 'Environmental Defense Fund'],
    },
    {
      name: 'Nature-Positive Business Targets Gaining Momentum',
      category: 'Biodiversity',
      description: 'Following the Kunming-Montreal Global Biodiversity Framework\'s "30x30" target, companies face mounting pressure to disclose biodiversity impacts via TNFD (Taskforce on Nature-related Financial Disclosures). AXA, GSK, and Holcim are among early adopters of nature-positive strategies, with ecosystem service valuations entering corporate balance sheet conversations.',
      keywords: ['TNFD', 'nature-positive', 'biodiversity corporate', 'ecosystem services', '30x30 framework'],
      sources: ['TNFD', 'Business for Nature', 'CDP Forests Report'],
    },
    {
      name: 'Green Hydrogen Industrial Decarbonisation Scale-Up',
      category: 'Clean Energy',
      description: 'Green hydrogen electrolyser costs have fallen 60% since 2020, making $2/kg green hydrogen achievable by 2030. Ørsted, Air Products, and Thyssenkrupp are building 10GW+ electrolysis capacity in EU, Australia, and Saudi Arabia. Steel, ammonia, and shipping — sectors resistant to direct electrification — are the primary decarbonisation targets.',
      keywords: ['green hydrogen', 'electrolysers', 'industrial decarbonisation', 'hydrogen economy', 'hard-to-abate sectors'],
      sources: ['International Energy Agency', 'Hydrogen Council', 'BloombergNEF Hydrogen'],
    },
    {
      name: 'Circular Economy Regulation Disrupting Manufacturing',
      category: 'Circular Economy',
      description: 'The EU\'s Ecodesign for Sustainable Products Regulation mandates repairability scores, recycled content requirements, and digital product passports across electronics, textiles, and construction materials by 2027. Apple\'s Self Repair Programme, Fairphone\'s modular design, and Interface\'s carpet take-back scheme are early circular economy models scaling under regulatory pressure.',
      keywords: ['circular economy', 'Ecodesign regulation', 'digital product passport', 'repairability', 'recycled content mandates'],
      sources: ['European Environment Agency', 'Ellen MacArthur Foundation', 'GreenBiz'],
    },
    {
      name: 'Climate Tech Investment Resetting After Peak',
      category: 'Investment & Finance',
      description: 'Climate tech VC investment normalised to $50B in 2025 after the $70B peak of 2022, with capital concentrating in heat pumps, long-duration storage, industrial heat electrification, and direct air capture rather than early-stage solar and wind. Breakthrough Energy, Lowercarbon Capital, and DOE Loan Programs Office are anchoring the capital stack for frontier climate technologies.',
      keywords: ['climate tech investment', 'clean tech VC', 'direct air capture', 'long-duration storage', 'Breakthrough Energy'],
      sources: ['BloombergNEF Clean Energy Investment', 'Cleantech Group', 'PwC State of Climate Tech'],
    },
  ],

  // ── MEDIA & ENTERTAINMENT ────────────────────────────────────────────────
  'media-entertainment': [
    {
      name: 'Streaming Profitability Era & Password Sharing Crackdown',
      category: 'Streaming',
      description: 'Netflix\'s password-sharing enforcement added 19M subscribers in 2023, triggering industry-wide crackdowns. Disney+, Peacock, and Max have all shifted from growth-at-all-costs to ARPU optimisation, with ad-supported tiers now accounting for 35%+ of new subscribers. Streaming has reached profitability maturity — the land-grab phase is over.',
      keywords: ['streaming profitability', 'AVOD growth', 'SVOD churn', 'ad-supported streaming', 'Netflix profitability'],
      sources: ['Variety', 'The Hollywood Reporter', 'Ampere Analysis'],
    },
    {
      name: 'Sports Rights Driving Streaming Bundle Wars',
      category: 'Live Sports',
      description: 'Apple TV+\'s $2.5B MLS deal, Amazon\'s NFL Thursday Night Football, and the NBA\'s landmark $76B media rights package (split between NBC, Amazon, and ESPN/ABC) are making live sports the premium driver of streaming bundle adoption. Regional Sports Network bankruptcy is accelerating the migration of local sports to streaming platforms.',
      keywords: ['sports streaming rights', 'NBA media deal', 'live sports streaming', 'sports bundle', 'RSN collapse'],
      sources: ['Sports Business Journal', 'Sportico', 'The Athletic'],
    },
    {
      name: 'AI Content Creation Disrupting Production Economics',
      category: 'AI in Media',
      description: 'Sora-generated video backgrounds, ElevenLabs voice cloning, and Runway ML\'s AI editing are reducing production costs 40-70% for short-form content. WGA and SAG-AFTRA\'s 2023 strikes established AI guardrails in Hollywood, but independent creators leveraging AI tools are democratising content production at unprecedented scale.',
      keywords: ['AI video generation', 'Sora', 'Runway ML', 'AI voice cloning', 'content production AI'],
      sources: ['The Hollywood Reporter', 'Variety AI', 'Screen Actors Guild'],
    },
    {
      name: 'Gaming Revenue Surpassing Film & Music Combined',
      category: 'Gaming',
      description: 'Global gaming revenue surpassed $250B in 2025, exceeding film and recorded music combined. Mobile gaming (50% of market), live service game models (Fortnite, GTA Online), and in-game virtual economies are redefining entertainment monetisation. Microsoft\'s Xbox Game Pass ($29B ARR) and Sony\'s PlayStation Plus are the streaming models for games.',
      keywords: ['gaming revenue', 'live service games', 'mobile gaming', 'Xbox Game Pass', 'gaming economy'],
      sources: ['Newzoo Global Games Market Report', 'Activision Blizzard IR', 'SuperData Research'],
    },
    {
      name: 'Creator Economy Institutionalisation',
      category: 'Creator Economy',
      description: 'The creator economy has matured from side-hustle to institutional — MrBeast\'s $700M valuation, Substack\'s 2M paid subscriber writers, and Patreon\'s 250K creators earning over $1K/month signal professional creator businesses. Brand creator partnerships average $65K per campaign at 1M+ follower tier, and creator studios (Night, Creator Corp) are building creator media companies.',
      keywords: ['creator economy', 'creator monetisation', 'Substack creators', 'creator brand deals', 'MrBeast'],
      sources: ['Influencer Marketing Hub', 'Creator Economy Report', 'Axios Media Trends'],
    },
    {
      name: 'Podcast Industry Professionalization & M&A',
      category: 'Audio',
      description: 'Spotify\'s $1B+ podcast acquisitions (Joe Rogan, Ringer) are being rationalised as the company refocuses on advertising revenue over exclusive content. iHeart Media, Wondery (Amazon), and Pushkin Industries represent the professional studio tier, while dynamic ad insertion technology makes podcast advertising a $5B+ industry with measurable ROAS.',
      keywords: ['podcast advertising', 'podcast M&A', 'Spotify podcasts', 'dynamic ad insertion', 'podcast monetisation'],
      sources: ['Edison Research Podcast Consumer', 'IAB Podcast Revenue Report', 'Bloomberg Media'],
    },
  ],

  // ── AUTOMOTIVE & MOBILITY ─────────────────────────────────────────────────
  'automotive-mobility': [
    {
      name: 'EV Adoption Plateau & Range Anxiety Resolution',
      category: 'Electric Vehicles',
      description: 'US EV market share reached 9% in 2025 but faces a "chasm crossing" challenge: early adopters have converted, while mainstream buyers cite charging anxiety, cost premium, and apartment charging limitations. NACS standard adoption, Tesla Supercharger opening to competitors, and the $7,500 IRA tax credit are the key levers for crossing the 15% threshold.',
      keywords: ['EV adoption', 'EV charging infrastructure', 'NACS standard', 'range anxiety', 'EV incentives'],
      sources: ['BloombergNEF EV Outlook', 'ICCT', 'J.D. Power EV Consideration Study'],
    },
    {
      name: 'Autonomous Vehicle Commercial Deployment Reality',
      category: 'Autonomous Vehicles',
      description: 'Waymo\'s San Francisco and Phoenix robotaxi operations (50,000+ weekly trips) represent the gold standard of AV commercialisation, while Tesla\'s "Full Self-Driving" v13 approaches highway autonomy. Autonomous trucking (Aurora, Kodiak) is closer to commercialisation than urban robotaxis, with Texas AV trucking corridors operational by end of 2026.',
      keywords: ['robotaxi', 'Waymo deployment', 'autonomous trucking', 'Aurora AV', 'FSD Tesla'],
      sources: ['Waymo Safety Report', 'RAND Autonomous Vehicle Policy', 'Aurora Innovation'],
    },
    {
      name: 'Software-Defined Vehicle Architecture Transformation',
      category: 'Vehicle Technology',
      description: 'Tesla, GM\'s Ultifi, and VW\'s E3 2.0 architecture are separating vehicle software from hardware — enabling OTA updates, subscription feature unlocks, and continuous product improvement post-purchase. Tier-1 suppliers (Aptiv, Continental) are pivoting from hardware to software, while Apple CarPlay Ultra and Google Android Automotive are battle-testing infotainment lock-in.',
      keywords: ['software-defined vehicle', 'OTA updates', 'vehicle subscription features', 'GM Ultifi', 'Android Automotive'],
      sources: ['McKinsey Center for Future Mobility', 'Automotive News', 'SAE International'],
    },
    {
      name: 'Mobility-as-a-Service Urban Integration',
      category: 'Urban Mobility',
      description: 'Cities like Helsinki (Whim), Singapore\'s MyTransport, and LA Metro\'s Micro Transit are operationalising MaaS — unified apps integrating public transit, bikeshare, scooters, rideshare, and parking into subscription mobility bundles. Uber\'s Transit partnership and Google Maps multi-modal booking are bringing MaaS mainstream beyond pilot cities.',
      keywords: ['mobility as a service', 'MaaS platforms', 'Whim app', 'multimodal transport', 'urban mobility integration'],
      sources: ['International Transport Forum', 'UITP', 'Deloitte Future of Mobility'],
    },
    {
      name: 'Chinese EV Brands Global Market Entry',
      category: 'Competitive Landscape',
      description: 'BYD surpassed Tesla in global EV sales volume in 2024, while NIO, Xpeng, and Li Auto are entering European markets with advanced features at 20-30% lower price points than German incumbents. US Section 301 tariffs (100% on Chinese EVs) and EU provisional tariffs are creating a bifurcated global market with significant implications for legacy OEM competitiveness.',
      keywords: ['BYD global expansion', 'Chinese EV brands', 'EV tariffs', 'NIO Europe', 'EV competitive landscape'],
      sources: ['S&P Global Mobility', 'Reuters Automotive', 'BloombergNEF EV Market'],
    },
    {
      name: 'EV Battery Supply Chain Localisation Race',
      category: 'Supply Chain',
      description: 'The IRA\'s domestic content requirements for the $7,500 EV tax credit are driving a $160B US battery manufacturing investment wave: Panasonic\'s Kansas gigafactory, Samsung SDI\'s Indiana plant, and LGES\'s Michigan expansion. Lithium mining in Nevada and Chile, manganese refining, and cathode manufacturing are the critical supply chain bottlenecks being addressed by 2027.',
      keywords: ['EV battery supply chain', 'gigafactory investment', 'IRA domestic content', 'lithium mining', 'battery localisation'],
      sources: ['BloombergNEF Battery Report', 'S&P Global Commodity Insights', 'DOE Critical Materials Strategy'],
    },
  ],

  // ── Original domains preserved ─────────────────────────────────────────────
  'technical-writing': [
    { name: 'Docs-as-Code Adoption Surge', category: 'Tooling', description: 'Engineering teams are migrating documentation into Git repos using Markdown, treating docs with the same rigor as source code — PRs, CI/CD, and automated testing included.', keywords: ['docs-as-code', 'git', 'markdown', 'CI/CD', 'version control'], sources: ['Write the Docs', 'Every Page is Page One', 'I\'d Rather Be Writing'] },
    { name: 'AI-Assisted API Reference Generation', category: 'AI Tools', description: 'Large language models are being fine-tuned on OpenAPI specs to auto-generate, review, and keep API reference docs in sync with code changes continuously.', keywords: ['OpenAPI', 'LLM', 'API reference', 'automation', 'developer experience'], sources: ['Stripe Dev Blog', 'Stoplight', 'Redocly'] },
    { name: 'Conversational Documentation UX', category: 'UX', description: 'Technical documentation portals are embedding chat interfaces trained on their own content, enabling developers to get instant, contextual answers instead of searching.', keywords: ['conversational UI', 'RAG', 'documentation chatbot', 'developer portal', 'search UX'], sources: ['Mintlify Blog', 'ReadMe', 'Docusaurus'] },
    { name: 'Minimalist Modular DITA Revival', category: 'Standards', description: 'Enterprise teams are rediscovering DITA\'s topic-based authoring model, now enhanced with lightweight YAML front matter and component content management systems.', keywords: ['DITA', 'structured authoring', 'CCMS', 'reuse', 'single-sourcing'], sources: ['The Content Wrangler', 'CIDM', 'Heretto'] },
    { name: 'Video-First Technical Tutorials', category: 'Format Shift', description: 'Developer audiences increasingly prefer short-form video walkthroughs over long prose, driving technical writers to adopt screencasting and narrated demos as primary content formats.', keywords: ['video docs', 'screencasting', 'tutorial', 'developer learning', 'multimedia'], sources: ['DevRel.co', 'Loom Blog', 'Scribe'] },
    { name: 'Accessibility in Technical Docs', category: 'Accessibility', description: 'WCAG 2.2 compliance is becoming a documentation quality gate, with teams auditing code samples, diagrams, and tables for screen-reader compatibility and colour contrast.', keywords: ['WCAG', 'accessibility', 'a11y', 'inclusive documentation', 'alt text'], sources: ['A11Y Project', 'Deque', 'Microsoft Docs'] },
  ],
  'digital-marketing': [
    { name: 'Zero-Click Search Optimisation', category: 'SEO', description: 'With Google\'s AI Overviews capturing over 40% of informational queries, marketers are restructuring content for featured snippets, knowledge panels, and answer-engine visibility.', keywords: ['zero-click', 'AI Overviews', 'featured snippets', 'SGE', 'answer engine optimisation'], sources: ['Search Engine Journal', 'Semrush Blog', 'Moz'] },
    { name: 'First-Party Data Infrastructure', category: 'Data Strategy', description: 'Post-cookie deprecation, brands are investing in CDPs, loyalty programmes, and interactive content to build owned data assets and reduce dependence on third-party targeting.', keywords: ['first-party data', 'CDP', 'cookie deprecation', 'data clean room', 'identity resolution'], sources: ['Marketing Week', 'Digiday', 'MarTech Alliance'] },
    { name: 'Generative AI Content at Scale', category: 'Content Production', description: 'Marketing teams are building AI content pipelines that produce personalised variants at scale — email, landing pages, ad copy — with human editors governing quality and brand voice.', keywords: ['generative AI', 'content at scale', 'personalisation', 'brand voice', 'AI copywriting'], sources: ['HubSpot Blog', 'Content Marketing Institute', 'Forrester'] },
    { name: 'Influencer-to-Creator Economy Shift', category: 'Influencer', description: 'Brands are moving from one-off influencer deals to long-term creator partnerships, co-owned products, and revenue sharing — treating creators as media channels, not ad placements.', keywords: ['creator economy', 'creator partnerships', 'co-creation', 'UGC', 'affiliate 2.0'], sources: ['Later', 'CreatorIQ', 'Business Insider'] },
    { name: 'Contextual Targeting Renaissance', category: 'Advertising', description: 'Without third-party cookies, contextual ad placement — matching ads to page content rather than user profiles — is experiencing a data-driven renaissance with AI-powered semantic matching.', keywords: ['contextual targeting', 'semantic matching', 'cookieless advertising', 'brand safety', 'programmatic'], sources: ['AdExchanger', 'The Trade Desk', 'DoubleVerify'] },
    { name: 'Revenue Attribution Modelling', category: 'Analytics', description: 'B2B marketers are abandoning last-click attribution in favour of multi-touch and data-driven models, integrating CRM and ad data to prove true channel contribution to pipeline.', keywords: ['attribution modelling', 'multi-touch attribution', 'pipeline marketing', 'B2B analytics', 'data-driven'], sources: ['Terminus', 'Demandbase', 'Dreamdata'] },
  ],
  'social-media-marketing': [
    { name: 'Short-Form Video Content Dominance', category: 'Format', description: 'Reels, TikToks, and YouTube Shorts now drive 80% of social discovery, forcing brands to build native vertical-video production pipelines with platform-specific creative strategies.', keywords: ['short-form video', 'Reels', 'TikTok', 'Shorts', 'vertical video'], sources: ['Hootsuite', 'Social Media Examiner', 'Later'] },
    { name: 'Social Commerce Integration', category: 'Commerce', description: 'In-app shopping features across Instagram, TikTok Shop, and Pinterest are collapsing the funnel — enabling consumers to discover, evaluate, and purchase without leaving the platform.', keywords: ['social commerce', 'TikTok Shop', 'shoppable posts', 'live shopping', 'in-app checkout'], sources: ['Shopify Blog', 'eMarketer', 'Glossy'] },
    { name: 'Community-Led Growth Strategy', category: 'Community', description: 'Brands are shifting from broadcasting to community cultivation — building private Discord servers, Slack groups, and Circle communities to drive retention, advocacy, and product feedback loops.', keywords: ['community marketing', 'Discord', 'brand community', 'advocacy', 'CLG'], sources: ['CMX Hub', 'Common Room', 'Orbit Model'] },
    { name: 'AI-Powered Social Listening', category: 'AI Tools', description: 'Next-gen social listening tools use real-time LLM analysis to surface not just mentions but sentiment shifts, narrative emergence, and competitive positioning signals across platforms.', keywords: ['social listening', 'sentiment analysis', 'AI monitoring', 'narrative intelligence', 'competitive intel'], sources: ['Brandwatch', 'Sprout Social', 'Talkwalker'] },
    { name: 'LinkedIn Thought Leadership Boom', category: 'B2B Social', description: 'B2B brands are systematically activating employee voices on LinkedIn, turning founders, engineers, and product managers into thought leaders through ghostwriting and content programmes.', keywords: ['LinkedIn', 'thought leadership', 'employee advocacy', 'B2B content', 'executive branding'], sources: ['LinkedIn Marketing Blog', 'Shield Analytics', 'Richard van der Blom'] },
    { name: 'Authenticity Over Polish', category: 'Creative Strategy', description: 'Platform algorithms and audience research consistently show that lo-fi, authentic behind-the-scenes content outperforms high-production brand videos — reshaping creative budgets significantly.', keywords: ['authentic content', 'behind-the-scenes', 'lo-fi video', 'UGC strategy', 'raw content'], sources: ['Sprout Social', 'Buffer', 'Social Media Today'] },
  ],
  'agentic-ai-docs': [
    { name: 'RAG-Powered Documentation Assistants', category: 'AI Architecture', description: 'Retrieval-Augmented Generation is being deployed on documentation corpora, enabling developers to query docs in natural language and receive cited, accurate answers — reducing support ticket volume by up to 35%.', keywords: ['RAG', 'LLM', 'documentation assistant', 'semantic search', 'vector database'], sources: ['LlamaIndex Blog', 'Langchain Docs', 'Mintlify'] },
    { name: 'Autonomous Doc Freshness Agents', category: 'Automation', description: 'AI agents are being deployed to monitor code repositories, detect API changes, and automatically flag or update outdated documentation sections — closing the perpetual "docs lag" problem.', keywords: ['documentation automation', 'AI agents', 'doc freshness', 'code monitoring', 'changelog intelligence'], sources: ['GitHub Next', 'Vercel Blog', 'Stripe Engineering'] },
    { name: 'Multi-Modal Doc Generation', category: 'Content Generation', description: 'Agentic pipelines now generate documentation that combines prose, auto-rendered code examples, interactive diagrams, and video walkthroughs — all from a single structured prompt or spec file.', keywords: ['multi-modal AI', 'doc generation', 'interactive docs', 'automated diagrams', 'code examples'], sources: ['Anthropic', 'OpenAI DevDay', 'Val Town Blog'] },
    { name: 'Prompt Engineering as a Discipline', category: 'Skills', description: 'Technical writers are upskilling in prompt engineering to orchestrate AI writing assistants effectively — developing internal style prompt libraries, evaluation rubrics, and governance frameworks.', keywords: ['prompt engineering', 'AI writing', 'technical writer skills', 'style prompts', 'AI governance'], sources: ['Write the Docs', 'Every Frame a Painting', 'Anthropic Cookbook'] },
    { name: 'AI-Driven Localisation Pipelines', category: 'Localisation', description: 'Agentic localisation workflows translate, culturally adapt, and QA documentation across 20+ languages simultaneously, with human review reserved only for high-risk or brand-sensitive passages.', keywords: ['localisation', 'AI translation', 'cultural adaptation', 'i18n', 'multilingual docs'], sources: ['Phrase', 'Lokalise', 'Smartcat'] },
    { name: 'Documentation Quality Scoring with LLMs', category: 'Quality', description: 'Teams are using fine-tuned language models to automatically score documentation for clarity, completeness, technical accuracy, and developer experience — creating objective quality benchmarks.', keywords: ['doc quality', 'LLM evaluation', 'readability scoring', 'completeness check', 'DX metrics'], sources: ['Google Developer Blog', 'Stripe Docs', 'ReadMe Metrics'] },
  ],
  'product-management': [
    { name: 'Continuous Product Discovery', category: 'Methodology', description: 'Product teams are moving from quarterly user research to weekly discovery cadences — embedding real user insights into sprint cycles through rapid experiments, opportunity trees, and assumption testing.', keywords: ['continuous discovery', 'opportunity tree', 'user research', 'Teresa Torres', 'assumption testing'], sources: ['Product Talk', 'Lenny\'s Newsletter', 'Mind the Product'] },
    { name: 'Product-Led Growth at Enterprise Scale', category: 'Strategy', description: 'Enterprise SaaS companies are layering PLG motions — free trials, usage-based triggers, in-product onboarding — on top of traditional sales-led funnels to reduce CAC and accelerate time-to-value.', keywords: ['PLG', 'product-led growth', 'usage-based pricing', 'freemium', 'in-product onboarding'], sources: ['OpenView Partners', 'Amplitude Blog', 'ProductLed.com'] },
    { name: 'AI-Assisted Roadmap Prioritisation', category: 'AI Tools', description: 'PMs are using AI tools to synthesise customer feedback, support tickets, and market signals into scored opportunity backlogs — making prioritisation frameworks more data-driven and defensible.', keywords: ['roadmap prioritisation', 'AI product tools', 'customer feedback', 'opportunity scoring', 'feature prioritisation'], sources: ['Productboard', 'Aha!', 'Pendo Blog'] },
    { name: 'Outcome-Based Roadmaps', category: 'Planning', description: 'Feature-list roadmaps are giving way to outcome-oriented formats — tying initiatives to measurable business and customer outcomes with explicit hypotheses, making progress trackable and strategy visible.', keywords: ['outcome roadmap', 'OKRs', 'business outcomes', 'product strategy', 'hypothesis-driven'], sources: ['Intercom Product Blog', 'Gibson Biddle', 'Roman Pichler'] },
    { name: 'Embedded AI Features Race', category: 'Product Development', description: 'Every B2B product team is under pressure to ship AI-native features — copilots, auto-summaries, predictive suggestions — with product leaders needing frameworks to prioritise AI investment wisely.', keywords: ['AI features', 'copilot UX', 'AI product strategy', 'embedded AI', 'LLM integration'], sources: ['a16z', 'FirstMark', 'Lenny\'s Newsletter'] },
    { name: 'Jobs-to-Be-Done Framework Resurgence', category: 'Methodology', description: 'JTBD is experiencing a renaissance as teams seek frameworks that stay stable across market shifts — focusing on the underlying human motivations rather than fluctuating feature preferences.', keywords: ['JTBD', 'jobs-to-be-done', 'customer motivation', 'Christensen', 'outcome-driven innovation'], sources: ['HBS Working Knowledge', 'Strategyn', 'Bob Moesta'] },
  ],
  'developer-relations': [
    { name: 'Developer Experience as Competitive Moat', category: 'Strategy', description: 'API companies are treating DX — onboarding time, SDK quality, docs clarity, error messages — as a primary product differentiator, with DX engineering teams working alongside DevRel.', keywords: ['developer experience', 'DX engineering', 'API design', 'onboarding', 'time-to-first-call'], sources: ['Stripe Blog', 'Twilio DevRel', 'DX of APIs'] },
    { name: 'DevRel Attribution & ROI Measurement', category: 'Metrics', description: 'DevRel teams are building measurement frameworks that connect community activities — talks, tutorials, Discord engagement — to pipeline, retention, and product adoption metrics.', keywords: ['DevRel metrics', 'ROI', 'attribution', 'developer funnel', 'community analytics'], sources: ['DevRel.co', 'Orbit Model', 'Common Room'] },
    { name: 'OSS-Led Community Building', category: 'Community', description: 'Companies are open-sourcing core tools, frameworks, and SDKs as community-building strategies — creating developer gravity that generates genuine advocacy, contribution, and enterprise upsell paths.', keywords: ['open source', 'OSS strategy', 'community-led', 'GitHub stars', 'developer advocacy'], sources: ['COSS Community', 'Redpoint Ventures', 'Joseph Jacks'] },
    { name: 'AI-Powered Developer Onboarding', category: 'AI Tools', description: 'Interactive coding environments, AI tutors, and sandbox APIs with instant feedback loops are replacing static getting-started guides — reducing time-to-first-value from hours to minutes.', keywords: ['developer onboarding', 'interactive tutorials', 'AI tutor', 'sandbox API', 'TTFV'], sources: ['Replit', 'Val Town', 'Supabase Blog'] },
    { name: 'Technical Content as SEO Engine', category: 'Content', description: 'DevRel teams are producing long-form tutorials, comparison guides, and code-heavy blog posts that rank for high-intent developer search terms, driving organic developer acquisition at scale.', keywords: ['technical SEO', 'developer content', 'tutorial marketing', 'organic developer acquisition', 'DevRel content'], sources: ['Draft.dev', 'Ahrefs Blog', 'Neon Blog'] },
    { name: 'Community-to-Product Feedback Loops', category: 'Product Integration', description: 'Leading DevRel programmes are formalising mechanisms to route community signals — GitHub issues, Discord threads, Stack Overflow patterns — directly into product prioritisation processes.', keywords: ['developer feedback', 'community signals', 'product integration', 'GitHub issues', 'developer voice'], sources: ['Supabase', 'PlanetScale', 'Prisma Blog'] },
  ],
};

// Set of preset domain IDs that have local seeds (no AI call needed)
export const PRESET_DOMAIN_IDS: Set<string> = new Set(Object.keys(TREND_SEEDS));

// ── Fuzzy preset matching ────────────────────────────────────────────────────
// Maps common typed labels to preset domain IDs so "Hospitality" → 'hospitality' seeds

const LABEL_TO_PRESET_ID: Array<[RegExp, string]> = [
  [/hotel|hospitality|resort|lodging|accommodation|inn|motel/i, 'hospitality'],
  [/travel|tourism|airline|flight|cruise|destination/i, 'travel-tourism'],
  [/health|hospital|clinic|medical|pharma|doctor|patient|nursing/i, 'healthcare'],
  [/wellness|fitness|gym|spa|yoga|nutrition|wellbeing/i, 'wellness-fitness'],
  [/retail|ecommerce|e-commerce|shop|store|commerce|fashion(?!.*luxury)/i, 'retail'],
  [/luxury|fashion|haute|couture|jewel|watch|premium brand/i, 'luxury-goods'],
  [/fintech|bank|finance|payment|insurance|lending|credit|invest(?!ment.*real)/i, 'fintech'],
  [/invest|wealth|asset management|private equity|portfolio|fund/i, 'investment-wealth'],
  [/real estate|property|proptech|mortgage|reit|housing|apartment|landlord/i, 'real-estate'],
  [/education|edtech|school|university|college|learning|curriculum|student/i, 'education-edtech'],
  [/food|beverage|restaurant|catering|culinary|kitchen|cafe|bakery|brewery/i, 'food-beverage'],
  [/manufactur|factory|industrial|supply chain|logistics|warehouse/i, 'manufacturing'],
  [/hr|human resource|talent|recruit|employee|workforce|people ops/i, 'human-resources'],
  [/legal|law|lawyer|attorney|compliance|contract|litigation|barrister/i, 'legal-legaltech'],
  [/sustain|esg|green|climate|carbon|renewable|environment|clean energy/i, 'sustainability'],
  [/media|entertainment|film|music|gaming|streaming|podcast|content creator/i, 'media-entertainment'],
  [/automotive|car|vehicle|ev|electric vehicle|mobility|transport|fleet/i, 'automotive-mobility'],
  [/technical writ|documentation|docs|api doc|developer guide/i, 'technical-writing'],
  [/digital market|seo|sem|ppc|content market|email market/i, 'digital-marketing'],
  [/social media|instagram|tiktok|linkedin|twitter|facebook|influencer/i, 'social-media-marketing'],
  [/agentic ai|llm|gpt|generative ai|ai doc|ai writ/i, 'agentic-ai-docs'],
  [/product manag|roadmap|okr|plg|product-led/i, 'product-management'],
  [/developer rel|devrel|developer advocate|api evangel/i, 'developer-relations'],
];

function findPresetMatch(label: string): string | null {
  for (const [pattern, presetId] of LABEL_TO_PRESET_ID) {
    if (pattern.test(label)) return presetId;
  }
  return null;
}

// ── AI trend generation via Supabase Edge Function (scrape + OpenAI GPT-4o-mini) ────
// The edge function handles:
//   1. Fetching and parsing the website HTML (for URL domains)
//   2. Sending real scraped content to GPT-4o-mini for industry identification
//   3. Returning 6 accurate, domain-specific trending topics

// Direct AI fallback for trend generation — tries Groq first, then OpenAI
async function generateTrendsViaOpenAIDirect(
  domain: Domain,
  _unused: string   // kept for backwards compat; keys read from localStorage
): Promise<Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>> {
  const label = domain.label || domain.url || 'general business';
  const prompt = `You are a content strategist. Generate 6 trending blog topics for the "${label}" industry.
Return ONLY a JSON array (no markdown, no extra text):
[{"name":"Trend Title","category":"Category","description":"2-3 sentence description","keywords":["kw1","kw2","kw3","kw4"],"sources":["Source 1","Source 2"]}]`;

  const tryEndpoint = async (url: string, key: string, model: string) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
      body: JSON.stringify({ model, temperature: 0.7, max_tokens: 2000, messages: [{ role: 'user', content: prompt }] }),
    });
    if (!res.ok) return [];
    const data = await res.json() as { choices?: Array<{ message: { content: string } }> };
    const raw = data?.choices?.[0]?.message?.content ?? '';
    const cleaned = raw.replace(/```json|```/g, '').trim();
    try {
      const parsed = JSON.parse(cleaned);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  };

  // 1. Groq (free primary — 14,400 req/day)
  const groqKey = localStorage.getItem('uxcf_groq_key') || '';
  if (groqKey) {
    try {
      const results = await tryEndpoint('https://api.groq.com/openai/v1/chat/completions', groqKey, 'llama-3.3-70b-versatile');
      if (results.length >= 3) return results;
    } catch (e) { console.warn('[domainEngine] Groq trend gen failed:', e); }
  }

  // 2. Gemini 2.0 Flash (free fallback #1 — 1,500 req/day)
  const geminiKey = localStorage.getItem('uxcf_gemini_key') || '';
  if (geminiKey) {
    try {
      const label = domain.label || domain.url || 'general business';
      const geminiPrompt = `You are a content strategist. Generate 6 trending blog topics for the "${label}" industry.\nReturn ONLY a JSON array (no markdown):\n[{"name":"Trend Title","category":"Category","description":"2-3 sentence description","keywords":["kw1","kw2","kw3","kw4"],"sources":["Source 1","Source 2"]}]`;
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contents: [{ parts: [{ text: geminiPrompt }] }], generationConfig: { temperature: 0.7, maxOutputTokens: 2000 } }),
        }
      );
      if (res.ok) {
        const data = await res.json() as { candidates?: Array<{ content: { parts: Array<{ text: string }> } }> };
        const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
        const cleaned = raw.replace(/```json|```/g, '').trim();
        try {
          const parsed = JSON.parse(cleaned);
          if (Array.isArray(parsed) && parsed.length >= 3) return parsed;
        } catch { /* fall through */ }
      }
    } catch (e) { console.warn('[domainEngine] Gemini trend gen failed:', e); }
  }

  // 3. Together AI (free fallback #2 — no key needed for free model)
  try {
    const label = domain.label || domain.url || 'general business';
    const prompt = `You are a content strategist. Generate 6 trending blog topics for the "${label}" industry.\nReturn ONLY a JSON array (no markdown):\n[{"name":"Trend Title","category":"Category","description":"2-3 sentence description","keywords":["kw1","kw2","kw3","kw4"],"sources":["Source 1","Source 2"]}]`;
    const togetherKey = localStorage.getItem('uxcf_together_key') || '';
    const res = await fetch('https://api.together.xyz/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(togetherKey ? { 'Authorization': `Bearer ${togetherKey}` } : {}),
      },
      body: JSON.stringify({
        model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo-Free',
        temperature: 0.7,
        max_tokens: 2000,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (res.ok) {
      const data = await res.json() as { choices?: Array<{ message: { content: string } }> };
      const raw = data?.choices?.[0]?.message?.content ?? '';
      const cleaned = raw.replace(/```json|```/g, '').trim();
      try {
        const parsed = JSON.parse(cleaned);
        if (Array.isArray(parsed) && parsed.length >= 3) return parsed;
      } catch { /* fall through */ }
    }
  } catch (e) { console.warn('[domainEngine] Together AI trend gen failed:', e); }

  return [];
}

async function generateTrendsViaProxy(
  domain: Domain,
  proxyUrl: string,
  headers: Record<string, string> = {}
): Promise<Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>> {
  try {
    const payload: Record<string, string> = {};
    if (domain.url) {
      payload.url = domain.url;
    } else {
      payload.customLabel = domain.label;
      payload.url = '';
    }

    // Pass the active AI key to the proxy — Groq preferred
    const groqKey = localStorage.getItem('uxcf_groq_key') || '';
    const geminiKey = localStorage.getItem('uxcf_gemini_key') || '';
    if (groqKey) {
      payload.apiKey = groqKey;
      payload.provider = 'groq';
    } else if (geminiKey) {
      payload.apiKey = geminiKey;
      payload.provider = 'gemini';
    }

    console.info(`[domainEngine] Calling trends proxy (${proxyUrl}) for: ${domain.url || domain.label}`);

    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.warn('[domainEngine] Proxy HTTP error:', res.status);
      return [];
    }

    const data = await res.json() as { success?: boolean; trends?: Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>; error?: string; scraped?: { title?: string } };

    if (!data.success || !Array.isArray(data.trends) || data.trends.length === 0) {
      console.warn('[domainEngine] Proxy returned no trends:', data.error);
      return [];
    }

    console.info(`[domainEngine] Got ${data.trends.length} trends via proxy. Title: "${data.scraped?.title || 'n/a'}"`);
    return data.trends;
  } catch (err) {
    console.error('[domainEngine] generateTrendsViaProxy failed:', err);
    return [];
  }
}

// Wraps Cloudflare Worker / Supabase edge + direct OpenAI fallback
async function generateTrendsWithFallback(
  domain: Domain
): Promise<Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>> {
  // 1. Cloudflare Worker (preferred — never sleeps)
  const cfWorker = (import.meta.env.VITE_CF_TRENDS_WORKER as string) || '';
  if (cfWorker) {
    const cfResults = await generateTrendsViaProxy(domain, cfWorker);
    if (cfResults.length >= 3) return cfResults;
  }

  // 2. Supabase edge function (fallback if Supabase is active)
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) || '';
  const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '';
  if (supabaseUrl && supabaseAnonKey) {
    const sbResults = await generateTrendsViaProxy(
      domain,
      `${supabaseUrl}/functions/v1/scrape-and-generate-trends`,
      { 'apikey': supabaseAnonKey, 'Authorization': `Bearer ${supabaseAnonKey}` }
    );
    if (sbResults.length >= 3) return sbResults;
  }

  // 3. Direct AI call — Groq → Gemini → Together AI
  console.info('[domainEngine] Proxy unavailable, falling back to direct AI');
  const directResults = await generateTrendsViaOpenAIDirect(domain, '');
  if (directResults.length >= 3) return directResults;

  return [];
}

// ── Generic fallback seeds (absolute last resort) ────────────────────────────
const GENERIC_TREND_SEEDS = [
  { name: 'AI-Augmented Workflows', category: 'AI Integration', description: 'Practitioners are embedding large language models and agentic AI into core workflows — automating repetitive cognitive tasks while keeping human judgement at strategic decision points.', keywords: ['AI automation', 'agentic AI', 'LLM integration', 'workflow', 'human-in-the-loop'], sources: ['Harvard Business Review', 'McKinsey', 'MIT Sloan'] },
  { name: 'Community-Led Knowledge Sharing', category: 'Community', description: 'Peer-to-peer learning communities are displacing traditional conference and certification models — with Discord servers, newsletters, and cohort courses becoming primary professional development channels.', keywords: ['community', 'peer learning', 'knowledge sharing', 'cohort', 'newsletter'], sources: ["Lenny's Newsletter", 'On Deck', 'Maven'] },
  { name: 'Data-Driven Decision Making', category: 'Analytics', description: 'Quantitative evidence is increasingly expected at every level of decision-making, with teams building internal dashboards, A/B testing cultures, and experiment-first frameworks.', keywords: ['data-driven', 'A/B testing', 'analytics', 'experimentation', 'metrics'], sources: ['Amplitude', 'Mixpanel Blog', 'Reforge'] },
  { name: 'Async-First Remote Collaboration', category: 'Work Patterns', description: 'Distributed teams are redesigning collaboration around asynchronous-first norms — Loom videos, written decision logs, and structured deep work blocks replacing synchronous meeting culture.', keywords: ['async', 'remote work', 'deep work', 'distributed teams', 'documentation culture'], sources: ['GitLab Handbook', 'Notion Blog', '37signals'] },
  { name: 'Micro-Content Atomisation Strategy', category: 'Content', description: 'Long-form content is being systematically atomised into platform-native micro-content — turning one research report into dozens of social posts, clips, infographics, and email sequences.', keywords: ['content atomisation', 'micro-content', 'content repurposing', 'omnichannel', 'content strategy'], sources: ['Content Marketing Institute', 'Gary Vaynerchuk', 'Repurpose.io'] },
  { name: 'Ethical & Responsible Practice Standards', category: 'Ethics', description: 'Practitioners across disciplines are developing ethical frameworks, accountability standards, and review processes to ensure their work avoids bias, respects privacy, and creates equitable outcomes.', keywords: ['ethics', 'responsible practice', 'accountability', 'bias', 'equity'], sources: ['ACM Ethics', 'IEEE', 'Partnership on AI'] },
];

// ── Trend Generation Helpers ──────────────────────────────────────────────────

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function seedsToTrends(
  seeds: Array<{ name: string; category: string; description: string; keywords: string[]; sources: string[] }>,
  domainId: string
): DiscoveredTrend[] {
  const shuffled = shuffle(seeds).slice(0, 6);
  const statuses: DiscoveredTrend['status'][] = ['emerging', 'trending', 'trending', 'stable'];
  const sentiments: DiscoveredTrend['sentiment'][] = ['positive', 'positive', 'neutral'];
  return shuffled.map((seed, i) => ({
    id: `${domainId}-trend-${i}-${Date.now()}`,
    domainId,
    name: seed.name,
    description: seed.description,
    category: seed.category,
    popularity: randomBetween(42, 98),
    growthRate: randomBetween(18, 94),
    keywords: seed.keywords,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    sentiment: sentiments[Math.floor(Math.random() * sentiments.length)],
    relevanceScore: randomBetween(55, 99),
    sources: seed.sources,
    discoveredAt: new Date().toISOString(),
  })).sort((a, b) => b.relevanceScore - a.relevanceScore);
}

/** Sync generator — returns immediately from preset seeds. */
export function generateTrendsForDomain(domain: Domain): DiscoveredTrend[] {
  const seeds = TREND_SEEDS[domain.id];
  if (seeds) return seedsToTrends(seeds, domain.id);
  // For unknown domains return generic immediately; async version below will replace
  return seedsToTrends(GENERIC_TREND_SEEDS, domain.id);
}

/** Async generator — works for all domains.
 *  Priority order:
 *  1. URL domain (.url set) → edge function scrapes site + GPT-4o-mini identifies industry
 *  2. Exact preset ID match (local seeds, instant)
 *  3. Fuzzy label match for typed custom text → preset seeds
 *  4. Edge function with custom label → GPT-4o-mini generates industry-specific trends
 *  5. Generic seeds (absolute last resort)
 */
export async function generateTrendsForDomainAsync(domain: Domain): Promise<DiscoveredTrend[]> {
  // 1. URL domain — try edge function (scrapes site) + direct OpenAI fallback
  if (domain.url) {
    console.info(`[domainEngine] URL domain → scrape+AI for: ${domain.url}`);
    const aiSeeds = await generateTrendsWithFallback(domain);
    if (aiSeeds.length >= 3) return seedsToTrends(aiSeeds, domain.id);
    return seedsToTrends(GENERIC_TREND_SEEDS, domain.id);
  }

  // 2. Exact preset match (preset chip selections — instant, no AI needed)
  const exactSeeds = TREND_SEEDS[domain.id];
  if (exactSeeds) return seedsToTrends(exactSeeds, domain.id);

  // 3. Fuzzy label match — ONLY for manually typed custom text (no .url)
  const fuzzyId = findPresetMatch(domain.label);
  if (fuzzyId && TREND_SEEDS[fuzzyId]) {
    console.info(`[domainEngine] Fuzzy match: "${domain.label}" → preset "${fuzzyId}"`);
    return seedsToTrends(TREND_SEEDS[fuzzyId], domain.id);
  }

  // 4. Custom label — try edge function + direct OpenAI fallback
  console.info(`[domainEngine] Custom label → AI for: ${domain.label}`);
  const aiSeeds = await generateTrendsWithFallback(domain);
  if (aiSeeds.length >= 3) return seedsToTrends(aiSeeds, domain.id);

  // 5. Absolute last resort
  return seedsToTrends(GENERIC_TREND_SEEDS, domain.id);
}

// ── Persistence ─────────────────────────────────────────────────────────────

const SESSION_KEY = 'uxcf_domain_session';
const HISTORY_KEY = 'uxcf_domain_history';

export function saveSession(session: DomainSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  const history = getDomainHistory();
  if (!history.find((d) => d.id === session.domain.id)) {
    history.unshift(session.domain);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 10)));
  }
}

export function getLastSession(): DomainSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function getDomainHistory(): Domain[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}
